import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Transaction,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { recordWorkspaceActivity } from '../services/activityService'
import { resolveMemberAiBudgetAndConsumption } from '../utils/aiBudget'
import type { ChecklistItem } from '../types/timeline'
import type {
  CanvasElement,
  ChecklistItem as FlatChecklistItem,
  DocumentLock,
  ElementProviderMappings,
  UserRole,
  WorkspaceMember,
} from '../types/schema'
import { TIMELINE_BOARD_KEYS, type TimelineBoardKey } from '../types/schema'
import type { TimelineWorkflowStatus } from '../types/schema'
import type { CanvasViewportState, CreateTimelineElementInput, MarkdownSyncMetadata, MockExternalCalendarEvent, TimelineElement, TimelineElementType } from '../types/timelineElement'
export type { CanvasViewportState, CreateTimelineElementInput, MarkdownSyncMetadata, MockExternalCalendarEvent, TimelineElement, TimelineElementType } from '../types/timelineElement'
import { useAuth } from './useAuth'
import { useDisplayName } from '../hooks/useDisplayName'
import { useAppDataRefreshSignal } from '../hooks/useAppDataRefreshSignal'
import { useWorkspace } from './useWorkspace'
import { useMyAccessScope } from '../hooks/useMyAccessScope'
import { setActiveAccessScope } from '../services/accessScopeService'
import { isHelperSyncSession, syncBridgeEngine } from '../services/syncBridgeEngine'
import { applyStripboardDelta as callStripboardDelta, createDeltaOperationId } from '../services/stripboardDeltaService'
import { claimPersonalCreditReward } from '../services/personalCreditRewardService'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import { isWorkspaceDemoUnlocked } from '../utils/demoUnlock'
import { isPersonalWorkspace } from '../utils/workspaceKind'
import { isHelperOnlyAccount } from '../utils/helperMembership'
import { isWorkspaceRole, parseWorkspaceRole, roleCanPerform, type WorkspaceAction } from '../utils/workspacePermissions'
import {
  isChecklistHelperVisible,
  updateChecklistDependencies,
  updateChecklistWorkflowStatus,
} from '../services/checklistMutationService'
import {
  emptyBookCurrentPageMap,
  projectDefinitionsToChecklistItems,
  type BookCurrentPageMap,
} from '../services/checklistViewAdapter'
import { mirrorRecurringWindowsForParent } from '../services/recurringWindowMirror'
import {
  bookIdFor,
  CHECKLISTS_COL,
  type ChecklistBookPage,
  type ChecklistDefinition,
  type ChecklistDetailVersion,
  type ChecklistLayoutVersion,
  type ChecklistType,
} from '../types/checklistBooks'
import { pageKeyFor } from '../services/checklistBookService'
import { getLayoutVersion } from '../services/layoutVersionService'
import { getDetailVersion } from '../services/checklistDetailVersionService'
import { bootstrapChecklistsOnBoard, writeChecklistUpdate } from '../services/checklistWriter'
import type { StripboardDelta, StripboardDeltaResult, StripboardRecordKind } from '../types/stripboard'
import {
  TimelineContext,
  type TimelineContextValue,
  elementQueryPlan,
  createScopedElementsCollector,
  resolveCanPerformOnNode,
} from './timelineContextCore'

import { getWorkspaceLimitValue } from '../utils/workspaceLimits'
import { getWrapperRole } from '../utils/wrapperRole'
import { emitExperienceEvent } from '../utils/experienceEvents'
import {
  buildDisplayId,
  normalizeRoutePath,
  placementSignature,
  segmentForOrdinal,
} from '../utils/publicIds'
import { getExploreWorkspaceSeedByWorkspaceId } from '../services/exploreWorkspaceSeeds'
import { isEduDemoWorkspace, getEduDemoDataset, getEduDemoDatasetSync } from '../services/eduDemo'
import { isCountedElement, isSyntheticParentId, CAP_KEY_TO_FEATURE_KEY, type CountedBoard } from '../config/elementCounting'
import { WORKSPACE_COUNT_HARD_CAPS } from '../config/workspaceLimitCaps'
import { runChecklistCreateTransaction, checkChecklistsSubtreeRoom } from '../services/checklistCounterTransaction'
import { getNlsString } from '../nls/nlsRuntime'
import { emitToast } from './ToastContext'
import {
  binElements,
  restoreElements,
  moveElements,
  reparentElements,
  deleteForeverElements,
} from '../services/elementRestructureService'

export const WORKSPACE_DEFAULT_CHECKLIST_ID = 'workspace-default-checklist'

const EMPTY_LOCKS: DocumentLock[] = []

const MOCK_EXTERNAL_EVENTS: MockExternalCalendarEvent[] = [
  {
    id: 'calendar-main-stage-audio-rig-arrival',
    title: 'Main Stage Audio Rig Arrival - 10:00 AM',
    startDateTime: '2026-06-05T10:00:00+05:30',
    description: 'Logistics crew receives and stages audio racks before main stage power checks.',
  },
  {
    id: 'calendar-talent-green-room-setup',
    title: 'Talent Green Room Setup - 01:45 PM',
    startDateTime: '2026-06-05T13:45:00+05:30',
    description: 'Prepare green room hospitality, wardrobe rack, mirror stations, and security handoff.',
  },
  {
    id: 'calendar-indie-film-camera-pack-b-return',
    title: 'Indie Film Camera Pack B Return - 06:00 PM',
    startDateTime: '2026-06-05T18:00:00+05:30',
    description: 'Return camera body, lens kit, battery stack, and media shuttle cases to rental desk.',
  },
  {
    id: 'calendar-talent-call',
    title: 'Principal Talent Call Sheet Sync - 06:00 PM',
    startDateTime: '2026-06-05T18:00:00+05:30',
    description: 'Reconcile transport, greenroom, wardrobe, and security timing.',
  },
]

const normalizeBoardKey = (value: unknown): TimelineBoardKey =>
  TIMELINE_BOARD_KEYS.includes(value as TimelineBoardKey) ? (value as TimelineBoardKey) : 'DASHBOARD'

const workflowStatusToBoardStatus = (status: FlatChecklistItem['workflowStatus']): TimelineWorkflowStatus | null => {
  if (status === 'CREATED' || status === 'ASSIGNED' || status === 'ASSESSING' || status === 'ACCEPTED') return 'TODO'
  if (status === 'ONGOING' || status === 'POSTPONED') return 'IN_PROGRESS'
  if (status === 'COMPLETED' || status === 'NOT_REQUIRED') return 'COMPLETE'
  return null
}

const normalizeChecklistItems = (items: unknown): ChecklistItem[] => {
  if (!Array.isArray(items)) return []

  return items
    .map((item, index): ChecklistItem | null => {
      if (!item || typeof item !== 'object') return null
      const candidate = item as Partial<ChecklistItem>
      const text = typeof candidate.text === 'string' ? candidate.text.trim() : ''
      if (!text) return null

      return {
        id: typeof candidate.id === 'string' && candidate.id.trim()
          ? candidate.id
          : `checklist-${index}-${Date.now()}`,
        text,
        description: typeof candidate.description === 'string' ? candidate.description : undefined,
        completionCriteria: typeof candidate.completionCriteria === 'string' ? candidate.completionCriteria : undefined,
        isCompleted: Boolean(candidate.isCompleted),
        weightage: typeof candidate.weightage === 'number' && candidate.weightage > 1
          ? candidate.weightage
          : 1,
        complexityStars: typeof candidate.complexityStars === 'number' ? candidate.complexityStars : undefined,
        urgencyStars: typeof candidate.urgencyStars === 'number' ? candidate.urgencyStars : undefined,
        importanceStars: typeof candidate.importanceStars === 'number' ? candidate.importanceStars : undefined,
        estimatedHours: typeof candidate.estimatedHours === 'number' ? candidate.estimatedHours : candidate.estimatedHours === null ? null : undefined,
        actualCost: typeof candidate.actualCost === 'number' ? candidate.actualCost : candidate.actualCost === null ? null : undefined,
        currencyCode: typeof candidate.currencyCode === 'string' ? candidate.currencyCode : undefined,
        budgetLimit: typeof candidate.budgetLimit === 'number' ? candidate.budgetLimit : candidate.budgetLimit === null ? null : undefined,
        actualSpend: typeof candidate.actualSpend === 'number' ? candidate.actualSpend : undefined,
        estimatedCost: typeof candidate.estimatedCost === 'number' ? candidate.estimatedCost : candidate.estimatedCost === null ? null : undefined,
        soldUnits: typeof candidate.soldUnits === 'number' ? candidate.soldUnits : candidate.soldUnits === null ? null : undefined,
        scheduleMode: candidate.scheduleMode === 'DURATION' ? 'DURATION' : candidate.scheduleMode === 'DATED' ? 'DATED' : undefined,
        scheduleStartDate: typeof candidate.scheduleStartDate === 'string' ? candidate.scheduleStartDate : candidate.scheduleStartDate === null ? null : undefined,
        scheduleEndDate: typeof candidate.scheduleEndDate === 'string' ? candidate.scheduleEndDate : candidate.scheduleEndDate === null ? null : undefined,
        hasTimeWindow: typeof candidate.hasTimeWindow === 'boolean' ? candidate.hasTimeWindow : undefined,
        scheduleTimeFrom: typeof candidate.scheduleTimeFrom === 'string' ? candidate.scheduleTimeFrom : candidate.scheduleTimeFrom === null ? null : undefined,
        scheduleTimeTo: typeof candidate.scheduleTimeTo === 'string' ? candidate.scheduleTimeTo : candidate.scheduleTimeTo === null ? null : undefined,
        blockedByNodeIds: Array.isArray(candidate.blockedByNodeIds) ? candidate.blockedByNodeIds : undefined,
        isBlockerForNodeIds: Array.isArray(candidate.isBlockerForNodeIds) ? candidate.isBlockerForNodeIds : undefined,
        workflowStatus: typeof candidate.workflowStatus === 'string' ? candidate.workflowStatus : undefined,
        // Preserve the recurring cadence tag AI (and manual authoring) sets so
        // syncChecklistBoardItems can route DAILY / WEEKLY items into their
        // correct book. Without this line every AI-generated checklist
        // silently defaults to INDIVIDUAL because the field was dropped here.
        checklistType: candidate.checklistType === 'DAILY' || candidate.checklistType === 'WEEKLY' || candidate.checklistType === 'INDIVIDUAL'
          ? candidate.checklistType
          : undefined,
        requiresProofFile: typeof candidate.requiresProofFile === 'boolean' ? candidate.requiresProofFile : undefined,
        requiresLocationPin: typeof candidate.requiresLocationPin === 'boolean' ? candidate.requiresLocationPin : undefined,
        cardBackgroundFileId: typeof candidate.cardBackgroundFileId === 'string' ? candidate.cardBackgroundFileId : candidate.cardBackgroundFileId === null ? null : undefined,
        cardBackgroundUrl: typeof candidate.cardBackgroundUrl === 'string' ? candidate.cardBackgroundUrl : candidate.cardBackgroundUrl === null ? null : undefined,
        cardBackgroundFileName: typeof candidate.cardBackgroundFileName === 'string' ? candidate.cardBackgroundFileName : candidate.cardBackgroundFileName === null ? null : undefined,
        cardBackgroundStoragePath: typeof candidate.cardBackgroundStoragePath === 'string' ? candidate.cardBackgroundStoragePath : candidate.cardBackgroundStoragePath === null ? null : undefined,
        isHelperVisible: isChecklistHelperVisible(candidate.isHelperVisible),
        assigneeId: typeof candidate.assigneeId === 'string' ? candidate.assigneeId : candidate.assigneeId === null ? null : undefined,
        assignedAt: typeof candidate.assignedAt === 'string' ? candidate.assignedAt : candidate.assignedAt === null ? null : undefined,
        scopeRead: Array.isArray(candidate.scopeRead)
          ? candidate.scopeRead.filter((userId): userId is string => typeof userId === 'string')
          : undefined,
        scopeWrite: Array.isArray(candidate.scopeWrite)
          ? candidate.scopeWrite.filter((userId): userId is string => typeof userId === 'string')
          : undefined,
        dependencyNodeIds: Array.isArray(candidate.dependencyNodeIds)
          ? candidate.dependencyNodeIds.filter((nodeId): nodeId is string => typeof nodeId === 'string')
          : undefined,
        stockRequirements: Array.isArray(candidate.stockRequirements)
          ? candidate.stockRequirements
              .map((requirement) => {
                if (!requirement || typeof requirement !== 'object') return null
                const stockRequirement = requirement as NonNullable<ChecklistItem['stockRequirements']>[number]
                if (typeof stockRequirement.stockItemId !== 'string' || !stockRequirement.stockItemId.trim()) return null
                return {
                  stockItemId: stockRequirement.stockItemId,
                  quantity: typeof stockRequirement.quantity === 'number' && Number.isFinite(stockRequirement.quantity)
                    ? Math.max(1, Math.round(stockRequirement.quantity))
                    : 1,
                  unitOverride: typeof stockRequirement.unitOverride === 'string' ? stockRequirement.unitOverride : stockRequirement.unitOverride === null ? null : undefined,
                  required: typeof stockRequirement.required === 'boolean' ? stockRequirement.required : undefined,
                }
              })
              .filter((requirement): requirement is NonNullable<typeof requirement> => Boolean(requirement))
          : undefined,
      }
    })
    .filter((item): item is ChecklistItem => Boolean(item))
}

const boardChecklistDocId = (elementId: string, checklistId: string) =>
  `${elementId}_${checklistId}`.replaceAll('/', '_')

const hasBoardPosition = (value: unknown): value is { x: number; y: number } => (
  Boolean(value) &&
  typeof value === 'object' &&
  Number.isFinite((value as { x?: unknown }).x) &&
  Number.isFinite((value as { y?: unknown }).y)
)

const hasToMillis = (value: unknown): value is { toMillis: () => number } => (
  Boolean(value) && typeof value === 'object' && typeof (value as { toMillis?: unknown }).toMillis === 'function'
)

const isPlainFirestoreRecord = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object') return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

const stripUndefinedForFirestore = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value
      .map((item) => stripUndefinedForFirestore(item))
      .filter((item) => item !== undefined) as T
  }

  if (!isPlainFirestoreRecord(value)) return value

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => item !== undefined)
      .map(([key, item]) => [key, stripUndefinedForFirestore(item)]),
  ) as T
}

const BOARD_NODE_WIDTH = 330
const BOARD_NODE_GAP = 44
const BOARD_ROW_HEIGHT = 120
const MAX_BOARD_ROW_ITEMS = 4

const toRecurringWindowIso = (value: unknown): string | null => {
  if (!(typeof value === 'string')) return null
  if (value.length < 10) return null
  const day = value.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

const syncChecklistBoardItems = async (
  elementId: string,
  workspaceId: string,
  workspaceSlug: string,
  board: TimelineBoardKey,
  parentRoutePath: string,
  checklist: ChecklistItem[],
  actor: { id: string; name: string },
  parentDates?: { startDate?: string | null; endDate?: string | null } | null,
  options?: { restricted?: boolean; uid?: string },
) => {
  // Parent-scoped recurring window. Populated once and reused across every
  // DAILY / WEEKLY child so their `recurringWindow` mirrors the parent at
  // create time — same semantics `createChecklistNative` uses for
  // user-created recurring items, and what `mirrorRecurringWindowsForParent`
  // maintains on subsequent parent date changes. Without this, an AI-drafted
  // DAILY item lands with no window and is always-visible on every day (the
  // filter falls back to true) — technically not blocked, but out of step
  // with user-created behavior.
  const recurringWindowStart = toRecurringWindowIso(parentDates?.startDate)
  const recurringWindowEnd = toRecurringWindowIso(parentDates?.endDate)
  const recurringWindow =
    recurringWindowStart && recurringWindowEnd && recurringWindowEnd >= recurringWindowStart
      ? {
        parentElementId: elementId,
        startDate: recurringWindowStart,
        endDate: recurringWindowEnd,
        syncedAt: serverTimestamp(),
      }
      : null
  const existingQuery = options?.restricted && options?.uid
    ? query(
        collection(firestoreDb, 'checklists'),
        where('workspaceId', '==', workspaceId),
        where('parentContainerId', '==', elementId),
        where('scopeRead', 'array-contains', options.uid),
      )
    : query(
        collection(firestoreDb, 'checklists'),
        where('workspaceId', '==', workspaceId),
        where('parentContainerId', '==', elementId),
      )
  const existingSnapshot = await getDocs(existingQuery)
  const existingBySourceId = new Map<string, { id: string; data: Record<string, unknown> }>()

  existingSnapshot.docs.forEach((snapshot) => {
    const data = snapshot.data()
    if (typeof data.sourceChecklistId === 'string') {
      existingBySourceId.set(data.sourceChecklistId, { id: snapshot.id, data })
    }
  })

  const batch = writeBatch(firestoreDb)
  const desiredSourceIds = new Set(checklist.map((checklistItem) => checklistItem.id))
  let hasBatchWrites = false
  // Newly-materialised definitions we need to attach to the
  // `checklist_books/{ws}_{type}` boards after the batch commits. Populated
  // during the forEach below whenever `existing` is null (fresh doc, not an
  // existing definition being merged). Bootstrap runs post-commit so the
  // definition doc actually exists when the board reads it.
  const newlyCreatedForBoard: Array<{
    id: string
    checklistType: 'INDIVIDUAL' | 'DAILY' | 'WEEKLY'
    position?: { x: number; y: number }
    // Parent-scoped date range mirrored from the owning Task/Goal/Subtask.
    // Consumed by bootstrapChecklistsOnBoard to spread each DAILY/WEEKLY
    // item across every layout-version group inside the window so divergent
    // future pages get a locally-resolved empty slot instead of inheriting
    // the item's today position (which was chosen against today's neighbours
    // and can collide with sibling items on other dates).
    recurringWindow?: { startDate: string; endDate: string }
  }> = []

  const itemsToCreate = checklist.filter((checklistItem) => !existingBySourceId.has(checklistItem.id))
  if (itemsToCreate.length > 0) {
    const roomCheck = await checkChecklistsSubtreeRoom(firestoreDb, {
      workspaceId,
      board,
      parentContainerId: elementId,
      subtreeSize: itemsToCreate.length,
    })
    if (!roomCheck.ok) {
      emitToast(roomCheck.message ?? 'Limit reached', 'error', {
        label: getNlsString('NLSChatActionUpgrade'),
        run: () => {
          window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
            detail: {
              featureKey: roomCheck.featureKey,
              limit: roomCheck.limit,
              currentUsage: roomCheck.currentUsage,
            },
          }))
        },
      })
      throw new Error(roomCheck.message)
    }
  }

  for (const [index, checklistItem] of checklist.entries()) {
    const existing = existingBySourceId.get(checklistItem.id)
    const targetId = existing?.id ?? boardChecklistDocId(elementId, checklistItem.id)
    const existingPosition = existing?.data.position
    const hasExistingPosition = hasBoardPosition(existingPosition)

    const routePath = `${normalizeRoutePath(parentRoutePath)}/c${index + 1}`.replace(/^\/+/, '')
    const rawWorkflowStatus = checklistItem.workflowStatus ?? existing?.data.workflowStatus
    const workflowStatus: FlatChecklistItem['workflowStatus'] = typeof rawWorkflowStatus === 'string'
      && ['CREATED', 'ASSIGNED', 'ASSESSING', 'ACCEPTED', 'ONGOING', 'COMPLETED', 'NOT_REQUIRED', 'POSTPONED'].includes(rawWorkflowStatus)
      ? rawWorkflowStatus as FlatChecklistItem['workflowStatus']
      : 'CREATED'
    // The Firestore create rule for `checklists/{id}` requires
    // isChecklistTypeValue(checklistType) — see firestore.rules §checklists.
    // Without this the whole batch.commit below rejects with
    // permission-denied, containers stay saved (previous loop already ran),
    // but the checklist docs never persist and the caller's Move-to-Manage
    // handoff aborts before onOpenManage(). Default to INDIVIDUAL when the
    // AI or user did not specify a recurrence.
    const rawChecklistType = checklistItem.checklistType ?? existing?.data.checklistType
    const checklistType: 'INDIVIDUAL' | 'DAILY' | 'WEEKLY' =
      rawChecklistType === 'DAILY' || rawChecklistType === 'WEEKLY'
        ? rawChecklistType
        : 'INDIVIDUAL'
    if (!existing) {
      newlyCreatedForBoard.push({
        id: targetId,
        checklistType,
        position: hasBoardPosition(existingPosition)
          ? existingPosition as { x: number; y: number }
          : undefined,
        // Only send the window for DAILY/WEEKLY. INDIVIDUAL books have a
        // single 'single' page so per-window spread is a no-op there — the
        // bulk today-write in bootstrapChecklistsOnBoard already covers them.
        ...((checklistType === 'DAILY' || checklistType === 'WEEKLY')
          && recurringWindow
          ? {
              recurringWindow: {
                startDate: recurringWindow.startDate,
                endDate: recurringWindow.endDate,
              },
            }
          : {}),
      })
    }
    const boardStatus = existing?.data.boardStatus === null || existing?.data.boardStatus === 'TODO' || existing?.data.boardStatus === 'IN_PROGRESS' || existing?.data.boardStatus === 'COMPLETE'
      ? existing.data.boardStatus
      : workflowStatusToBoardStatus(workflowStatus)
    const scheduleStartDate = checklistItem.scheduleStartDate ?? existing?.data.scheduleStartDate ?? null
    const scheduleEndDate = checklistItem.scheduleEndDate ?? existing?.data.scheduleEndDate ?? null
    const startDate = existing?.data.startDate ?? scheduleStartDate
    const endDate = existing?.data.endDate ?? scheduleEndDate
    const scheduleStartDay = typeof scheduleStartDate === 'string' ? scheduleStartDate.slice(0, 10) : ''
    const scheduleEndDay = typeof scheduleEndDate === 'string' ? scheduleEndDate.slice(0, 10) : ''
    const scheduleMode = scheduleStartDay && scheduleEndDay && scheduleStartDay !== scheduleEndDay
      ? 'DURATION' as const
      : checklistItem.scheduleMode ?? existing?.data.scheduleMode ?? 'DATED'

    const checklistDocPayload = stripUndefinedForFirestore({
      id: targetId,
      checklistType,
      sourceChecklistId: checklistItem.id,
      parentContainerId: elementId,
      workspaceId,
      workspaceSlug,
      board,
      parentRoutePath: normalizeRoutePath(parentRoutePath),
      routePath,
      publicId: buildDisplayId(workspaceSlug, board, routePath),
      title: checklistItem.text,
      boardStatus,
      priority: existing?.data.priority ?? Math.ceil(((checklistItem.urgencyStars ?? 1) + (checklistItem.importanceStars ?? 1)) / 2),
      startDate,
      endDate,
      recordVersion: existing?.data.recordVersion ?? 1,
      statusSyncOrigin: existing?.data.statusSyncOrigin ?? 'CREATE',
      statusSyncVersion: existing?.data.statusSyncVersion ?? 1,
      description: checklistItem.description ?? existing?.data.description ?? '',
      completionCriteria: checklistItem.completionCriteria ?? existing?.data.completionCriteria ?? '',
      budgetLimit: checklistItem.budgetLimit ?? existing?.data.budgetLimit ?? null,
      actualSpend: checklistItem.actualSpend ?? existing?.data.actualSpend ?? 0,
      estimatedCost: checklistItem.estimatedCost ?? existing?.data.estimatedCost ?? null,
      soldUnits: checklistItem.soldUnits ?? existing?.data.soldUnits ?? null,
      createdById: existing?.data.createdById ?? actor.id,
      createdByName: existing?.data.createdByName ?? actor.name,
      createdAt: existing?.data.createdAt ?? serverTimestamp(),
      assigneeId: checklistItem.assigneeId ?? existing?.data.assigneeId ?? null,
      assigneeName: existing?.data.assigneeName ?? null,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: actor.id,
      lastUpdatedByName: actor.name,
      isCompleted: checklistItem.isCompleted,
      isHelperVisible: isChecklistHelperVisible(checklistItem.isHelperVisible ?? existing?.data.isHelperVisible),
      weightage: typeof checklistItem.weightage === 'number' && checklistItem.weightage > 1
        ? checklistItem.weightage
        : 1,
      position: hasExistingPosition
        ? existingPosition
        : {
          x: 100 + (index % MAX_BOARD_ROW_ITEMS) * (BOARD_NODE_WIDTH + BOARD_NODE_GAP),
          y: Math.floor(index / MAX_BOARD_ROW_ITEMS) * BOARD_ROW_HEIGHT + 50,
        },
      prerequisites: Array.isArray(existing?.data.prerequisites) ? existing?.data.prerequisites : [],
      blockedByExternal: null,
      blockedByExternalId: null,
      interWorkspaceTargetId: null,
      complexityStars: checklistItem.complexityStars ?? existing?.data.complexityStars ?? 3,
      urgencyStars: checklistItem.urgencyStars ?? existing?.data.urgencyStars ?? 3,
      importanceStars: checklistItem.importanceStars ?? existing?.data.importanceStars ?? 3,
      estimatedHours: checklistItem.estimatedHours ?? existing?.data.estimatedHours ?? null,
      actualCost: checklistItem.actualCost ?? existing?.data.actualCost ?? null,
      currencyCode: checklistItem.currencyCode ?? existing?.data.currencyCode ?? 'USD',
      scheduleMode,
      scheduleStartDate,
      scheduleEndDate,
      hasTimeWindow: checklistItem.hasTimeWindow ?? existing?.data.hasTimeWindow ?? false,
      scheduleTimeFrom: checklistItem.scheduleTimeFrom ?? existing?.data.scheduleTimeFrom ?? null,
      scheduleTimeTo: checklistItem.scheduleTimeTo ?? existing?.data.scheduleTimeTo ?? null,
      cardBackgroundFileId: checklistItem.cardBackgroundFileId ?? existing?.data.cardBackgroundFileId ?? null,
      cardBackgroundUrl: checklistItem.cardBackgroundUrl ?? existing?.data.cardBackgroundUrl ?? null,
      cardBackgroundFileName: checklistItem.cardBackgroundFileName ?? existing?.data.cardBackgroundFileName ?? null,
      cardBackgroundStoragePath: checklistItem.cardBackgroundStoragePath ?? existing?.data.cardBackgroundStoragePath ?? null,
      dependencyNodeIds: checklistItem.dependencyNodeIds ?? existing?.data.dependencyNodeIds ?? [],
      stockRequirements: checklistItem.stockRequirements ?? existing?.data.stockRequirements ?? [],
      invalidDependencyNodeIds: existing?.data.invalidDependencyNodeIds ?? [],
      blockedByNodeIds: Array.isArray(existing?.data.blockedByNodeIds)
        ? existing.data.blockedByNodeIds.filter((link) => link && typeof link === 'object' && (link as { targetType?: unknown }).targetType === 'CHECKLIST')
        : [],
      isBlockerForNodeIds: Array.isArray(existing?.data.isBlockerForNodeIds)
        ? existing.data.isBlockerForNodeIds.filter((link) => link && typeof link === 'object' && (link as { targetType?: unknown }).targetType === 'CHECKLIST')
        : [],
      workflowStatus,
      requiresProofFile: checklistItem.requiresProofFile ?? existing?.data.requiresProofFile ?? false,
      requiresLocationPin: checklistItem.requiresLocationPin ?? existing?.data.requiresLocationPin ?? false,
      ...((checklistType === 'DAILY' || checklistType === 'WEEKLY') && recurringWindow
        ? { recurringWindow }
        : {}),
    })

    if (existing) {
      batch.set(doc(firestoreDb, 'checklists', targetId), checklistDocPayload, { merge: true })
      hasBatchWrites = true
    } else {
      await runChecklistCreateTransaction(firestoreDb, {
        workspaceId,
        board,
        parentContainerId: elementId,
        checklistRef: doc(firestoreDb, 'checklists', targetId),
        checklistData: checklistDocPayload,
      })
    }
  }

  if (hasBatchWrites) await batch.commit()

  // Book-model mirror for the same intent: run alongside the legacy batch
  // so `checklists/{id}` + `checklist_books/{ws}_{type}/pages/{today}` stay
  // in sync with the element's inline `checklist[]`. Uses the same doc ids
  // the batch wrote so both collections resolve to one entity.
  const bookActor = { uid: actor.id, displayName: actor.name }

  // Attach freshly-created definitions to the Individual / Daily / Weekly
  // boards. `writeChecklistUpdate` below only patches an EXISTING book page's
  // completion map — it does NOT bootstrap the book, page, or layout for a
  // new checklist. Without this step, AI-drafted checklists persist as
  // definitions and appear in element.checklist snapshots but never render
  // on the Checklists surface's boards. Non-throwing by design (see helper's
  // JSDoc) — a board-visibility failure never blocks the caller's
  // Move-to-Manage flow.
  if (newlyCreatedForBoard.length > 0) {
    await bootstrapChecklistsOnBoard(workspaceId, newlyCreatedForBoard, bookActor)
  }
  const desiredNativeWrites: Promise<unknown>[] = []
  checklist.forEach((checklistItem) => {
    const existing = existingBySourceId.get(checklistItem.id)
    const targetId = existing?.id ?? boardChecklistDocId(elementId, checklistItem.id)
    desiredNativeWrites.push(
      writeChecklistUpdate(
        targetId,
        {
          title: checklistItem.text,
          workspaceId,
          description: checklistItem.description ?? '',
          completionCriteria: checklistItem.completionCriteria ?? '',
          requiresProofFile: checklistItem.requiresProofFile ?? false,
          requiresLocationPin: checklistItem.requiresLocationPin ?? false,
        },
        bookActor,
      ).catch((err) => console.debug('[syncChecklistBoardItems] native mirror skipped', (err as Error)?.message)),
    )
  })
  existingSnapshot.docs.forEach((snapshot) => {
    const sourceChecklistId = snapshot.data().sourceChecklistId
    if (typeof sourceChecklistId === 'string' && !desiredSourceIds.has(sourceChecklistId)) {
      desiredNativeWrites.push(
        deleteChecklistNativeShim(snapshot.id, bookActor).catch((err) =>
          console.debug('[syncChecklistBoardItems] native delete skipped', (err as Error)?.message),
        ),
      )
    }
  })
  await Promise.all(desiredNativeWrites)
}

// Lazy import to avoid circular dep at module init.
const deleteChecklistNativeShim = async (id: string, actor: { uid: string; displayName?: string | null }) => {
  const { deleteChecklistNative } = await import('../services/checklistWriter')
  await deleteChecklistNative(id, actor)
}

export const normalizeElement = (id: string, data: Partial<TimelineElement>, workspaceId: string): TimelineElement => ({
  id,
  type: data.type ?? 'SUBTASK',
  title: data.title ?? 'Untitled',
  description: typeof data.description === 'string' ? data.description : '',
  weight: typeof data.weight === 'number' ? data.weight : 1,
  progress: typeof data.progress === 'number' ? data.progress : 0,
  parentId: data.parentId ?? null,
  board: normalizeBoardKey(data.board),
  publicId: typeof data.publicId === 'string' ? data.publicId : undefined,
  routePath: normalizeRoutePath(data.routePath),
  parentRoutePath: normalizeRoutePath(data.parentRoutePath),
  deletedFromBoard: TIMELINE_BOARD_KEYS.includes(data.deletedFromBoard as TimelineBoardKey)
    ? data.deletedFromBoard as TimelineBoardKey
    : null,
  deletedAt: data.deletedAt,
  workspaceId: data.workspaceId ?? workspaceId,
  startDate: typeof data.startDate === 'string' ? data.startDate : undefined,
  endDate: typeof data.endDate === 'string' ? data.endDate : undefined,
  boardStatus: data.boardStatus === null || data.boardStatus === 'TODO' || data.boardStatus === 'IN_PROGRESS' || data.boardStatus === 'COMPLETE'
    ? data.boardStatus
    : data.workflowStatus === 'TODO' || data.workflowStatus === 'IN_PROGRESS' || data.workflowStatus === 'COMPLETE'
      ? data.workflowStatus
      : data.startDate
        ? 'TODO'
        : null,
  workflowStatus: data.workflowStatus === 'TODO' || data.workflowStatus === 'IN_PROGRESS' || data.workflowStatus === 'COMPLETE'
    ? data.workflowStatus
    : undefined,
  priority: typeof data.priority === 'number' && Number.isFinite(data.priority)
    ? Math.max(1, Math.min(5, Math.round(data.priority)))
    : 1,
  dependencyNodeIds: Array.isArray(data.dependencyNodeIds)
    ? data.dependencyNodeIds.filter((nodeId): nodeId is string => typeof nodeId === 'string')
    : [],
  invalidDependencyNodeIds: Array.isArray(data.invalidDependencyNodeIds)
    ? data.invalidDependencyNodeIds.filter((nodeId): nodeId is string => typeof nodeId === 'string')
    : [],
  recordVersion: typeof data.recordVersion === 'number' && Number.isSafeInteger(data.recordVersion) && data.recordVersion > 0 ? data.recordVersion : 1,
  statusSyncOrigin: typeof data.statusSyncOrigin === 'string' ? data.statusSyncOrigin : null,
  statusSyncVersion: typeof data.statusSyncVersion === 'number' && Number.isSafeInteger(data.statusSyncVersion) ? data.statusSyncVersion : 0,
  attachedFormIds: Array.isArray(data.attachedFormIds) ? data.attachedFormIds.filter((formId): formId is string => typeof formId === 'string') : [],
  attachedDesignIds: Array.isArray(data.attachedDesignIds) ? data.attachedDesignIds.filter((designId): designId is string => typeof designId === 'string') : [],
  attachedUploadIds: Array.isArray(data.attachedUploadIds) ? data.attachedUploadIds.filter((uploadId): uploadId is string => typeof uploadId === 'string') : [],
  attachedLinkIds: Array.isArray(data.attachedLinkIds) ? data.attachedLinkIds.filter((linkId): linkId is string => typeof linkId === 'string') : [],
  budgetLimit: typeof data.budgetLimit === 'number' ? data.budgetLimit : null,
  estimatedCost: typeof data.estimatedCost === 'number' ? data.estimatedCost : null,
  actualSpend: typeof data.actualSpend === 'number' ? data.actualSpend : null,
  color: typeof data.color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(data.color) ? data.color as `#${string}` : undefined,
  spatialX: typeof data.spatialX === 'number' && Number.isFinite(data.spatialX) ? data.spatialX : undefined,
  spatialY: typeof data.spatialY === 'number' && Number.isFinite(data.spatialY) ? data.spatialY : undefined,
  spatialZone: typeof data.spatialZone === 'string' ? data.spatialZone : undefined,
  checklist: normalizeChecklistItems(data.checklist),
  readAccess: Array.isArray(data.readAccess) ? data.readAccess.filter((userId) => typeof userId === 'string') : [],
  writeAccess: Array.isArray(data.writeAccess) ? data.writeAccess.filter((userId) => typeof userId === 'string') : [],
  directReadAccess: Array.isArray(data.directReadAccess)
    ? data.directReadAccess.filter((userId) => typeof userId === 'string')
    : [],
  directWriteAccess: Array.isArray(data.directWriteAccess)
    ? data.directWriteAccess.filter((userId) => typeof userId === 'string')
    : [],
  scopeRead: Array.isArray(data.scopeRead)
    ? data.scopeRead.filter((userId): userId is string => typeof userId === 'string')
    : undefined,
  scopeWrite: Array.isArray(data.scopeWrite)
    ? data.scopeWrite.filter((userId): userId is string => typeof userId === 'string')
    : undefined,
  lastUpdatedBy: typeof data.lastUpdatedBy === 'string' ? data.lastUpdatedBy : undefined,
  lastUpdatedAt: data.lastUpdatedAt,
  createdAt: data.createdAt,
  completedAt: data.completedAt,
  providerMappings: data.providerMappings && typeof data.providerMappings === 'object'
    ? data.providerMappings as ElementProviderMappings
    : {},
  mapProviderId: typeof data.mapProviderId === 'string' ? data.mapProviderId : undefined,
})

const toSyncCanvasElement = (element: TimelineElement): CanvasElement => ({
  id: element.id,
  workspaceId: element.workspaceId,
  type: element.type,
  parentId: element.parentId,
  board: element.board,
  publicId: element.publicId,
  routePath: element.routePath,
  parentRoutePath: element.parentRoutePath,
  deletedFromBoard: element.deletedFromBoard,
  deletedAt: element.deletedAt,
  title: element.title,
  description: element.description ?? '',
  progress: element.progress,
  boardStatus: element.boardStatus,
  priority: element.priority,
  dependencyNodeIds: element.dependencyNodeIds,
  invalidDependencyNodeIds: element.invalidDependencyNodeIds,
  startDate: element.startDate ?? null,
  endDate: element.endDate ?? null,
  recordVersion: element.recordVersion,
  statusSyncOrigin: element.statusSyncOrigin,
  statusSyncVersion: element.statusSyncVersion,
  weightage: element.weight,
  assignedEditors: element.writeAccess,
})

const getBridgeChecklistItems = (
  elements: TimelineElement[],
  flatChecklistItems: FlatChecklistItem[],
): FlatChecklistItem[] => {
  const containersWithFlatItems = new Set(flatChecklistItems.map((item) => item.parentContainerId))
  const fallbackItems: FlatChecklistItem[] = []

  elements.forEach((element) => {
    if (containersWithFlatItems.has(element.id)) return

    element.checklist.forEach((item, index) => {
      fallbackItems.push({
        id: boardChecklistDocId(element.id, item.id),
        workspaceId: element.workspaceId,
        workspaceSlug: element.publicId?.split('-')[0],
        parentContainerId: element.id,
        board: element.board,
        parentRoutePath: normalizeRoutePath(element.routePath),
        routePath: `${normalizeRoutePath(element.routePath)}/c${index + 1}`.replace(/^\/+/, ''),
        publicId: element.publicId
          ? buildDisplayId(element.publicId.split('-')[0] ?? '', element.board, `${normalizeRoutePath(element.routePath)}/c${index + 1}`)
          : undefined,
        title: item.text,
        boardStatus: workflowStatusToBoardStatus(item.workflowStatus),
        priority: Math.ceil(((item.urgencyStars ?? 1) + (item.importanceStars ?? 1)) / 2),
        startDate: item.scheduleStartDate ?? null,
        endDate: item.scheduleEndDate ?? null,
        recordVersion: 1,
        statusSyncOrigin: 'LEGACY_PROJECTION',
        statusSyncVersion: 1,
        dependencyNodeIds: item.dependencyNodeIds ?? [],
        invalidDependencyNodeIds: [],
        isCompleted: item.isCompleted,
        isHelperVisible: isChecklistHelperVisible(item.isHelperVisible),
        weightage: typeof item.weightage === 'number' && item.weightage > 0 ? item.weightage : 1,
        position: { x: index, y: 0 },
        prerequisites: [],
        blockedByExternal: null,
      })
    })
  })

  return [...flatChecklistItems, ...fallbackItems]
}

const uniqueUserIds = (userIds: string[]) => [...new Set(userIds.filter(Boolean))]

const normalizeRole = (role: unknown): UserRole =>
  parseWorkspaceRole(role) ?? 'VIEWER'

const normalizeBoardRole = (boardRole: unknown): Exclude<UserRole, 'HELPER'> | null =>
  boardRole === 'ADMIN' || boardRole === 'EDITOR' || boardRole === 'VIEWER' || boardRole === 'READONLY' ? boardRole : null

const normalizeMemberProfile = (id: string, data: Partial<WorkspaceMember>): WorkspaceMember => ({
  id,
  workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
  uid: typeof data.uid === 'string' ? data.uid : undefined,
  userId: typeof data.userId === 'string' ? data.userId : undefined,
  user_email: typeof data.user_email === 'string' ? data.user_email : undefined,
  email: typeof data.email === 'string' ? data.email : undefined,
  role: normalizeRole(data.role),
  boardRoles: typeof data.boardRoles === 'object' && data.boardRoles !== null
    ? Object.fromEntries(
      Object.entries(data.boardRoles as Record<string, unknown>)
        .map(([board, role]) => [board, normalizeBoardRole(role)] as const)
        .filter(([board, role]) => TIMELINE_BOARD_KEYS.includes(board as TimelineBoardKey) && role !== null)
    ) as Partial<Record<TimelineBoardKey, UserRole>>
    : undefined,
  credits: typeof data.credits === 'number' ? data.credits : undefined,
  dailyCreditAllowance: typeof data.dailyCreditAllowance === 'number' ? data.dailyCreditAllowance : undefined,
  creditsConsumedToday: typeof data.creditsConsumedToday === 'number' ? data.creditsConsumedToday : undefined,
  status: data.status === 'ACTIVE' || data.status === 'PENDING' || data.status === 'DECLINED' || data.status === 'REVOKED'
    ? data.status
    : undefined,
})

const lockExpiryMs = (lock: DocumentLock) => {
  const expiresAt = lock.expiresAt
  if (hasToMillis(expiresAt)) return expiresAt.toMillis()
  if (expiresAt instanceof Date) return expiresAt.getTime()
  if (typeof expiresAt === 'number') return expiresAt
  return 0
}

const normalizeLock = (id: string, data: Partial<DocumentLock>): DocumentLock | null => {
  if (
    typeof data.workspaceId !== 'string' ||
    typeof data.lockedBy !== 'string' ||
    typeof data.lockedByName !== 'string'
  ) {
    return null
  }

  return {
    id,
    workspaceId: data.workspaceId,
    lockedBy: data.lockedBy,
    lockedByName: data.lockedByName,
    expiresAt: data.expiresAt,
  }
}

export const TimelineProvider = ({
  children,
  workspaceId,
  isPublicReadOnly = false,
}: PropsWithChildren<{ workspaceId: string; isPublicReadOnly?: boolean }>) => {
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const {
    activeWorkspaceId,
    activeRole,
    appMode,
    workspaces,
    canPerform,
  } = useWorkspace()
  const effectiveWorkspaceId = isPublicReadOnly ? workspaceId : activeWorkspaceId || workspaceId
  const {
    restricted: isAccessRestricted,
    helper: isAccessHelper,
    ready: isAccessScopeReady,
  } = useMyAccessScope(
    effectiveWorkspaceId,
    { isPublicReadOnly },
  )

  const isHelperAccount = isHelperOnlyAccount({ userProfile, currentUser, workspaces, activeRole })

  const hasDispatchedFirstSnapshotRef = useRef(false)
  const dispatchFirstSnapshot = useCallback(() => {
    if (hasDispatchedFirstSnapshotRef.current) return
    hasDispatchedFirstSnapshotRef.current = true
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('timeline:first-snapshot', {
          detail: { workspaceId: effectiveWorkspaceId || workspaceId },
        }),
      )
    }
  }, [effectiveWorkspaceId, workspaceId])

  useEffect(() => {
    if (isAccessScopeReady && effectiveWorkspaceId && currentUser?.uid) {
      setActiveAccessScope({
        workspaceId: effectiveWorkspaceId,
        uid: currentUser.uid,
        mode: isAccessHelper ? 'HELPER' : isAccessRestricted ? 'RESTRICTED' : 'ALL',
        restricted: isAccessRestricted,
      })
    } else {
      setActiveAccessScope(null)
    }
    return () => {
      setActiveAccessScope(null)
    }
  }, [isAccessScopeReady, effectiveWorkspaceId, currentUser?.uid, isAccessRestricted, isAccessHelper])
  const refreshToken = useAppDataRefreshSignal()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === effectiveWorkspaceId)
  const isPreviewWorkspace = activeWorkspace?.isPreview === true
  const activeWorkspaceSlug = activeWorkspace?.slug ?? effectiveWorkspaceId.toLowerCase()
  const [elements, setElements] = useState<TimelineElement[]>([])
  const [pathOnlyElementIds, setPathOnlyElementIds] = useState<Set<string>>(() => new Set())
  const [elementsLastSyncedAt, setElementsLastSyncedAt] = useState<Date | null>(null)
  const [elementsSubscriptionError, setElementsSubscriptionError] = useState<Error | null>(null)
  const [refreshNonce, setRefreshNonce] = useState(0)
  const refreshElementsSubscription = useCallback(() => setRefreshNonce((value) => value + 1), [])
  const [checklistItems, setChecklistItems] = useState<FlatChecklistItem[]>([])
  const [checklistsLoaded, setChecklistsLoaded] = useState(false)
  // Cover/Page/LayoutVersion book-model projection. Coexists with the
  // legacy `checklistItems` slice above during the cutover; the merged view
  // is exposed on the context value as `checklistItems`. See
  // src/services/checklistViewAdapter.ts.
  const [bookModelChecklistItems, setBookModelChecklistItems] = useState<FlatChecklistItem[]>([])
  // Recurring pages / settings are subscribed directly by the Recurring
  // canvas components, not surfaced on TimelineContext.
  const syncElementsRef = useRef<TimelineElement[]>([])
  const syncChecklistItemsRef = useRef<FlatChecklistItem[]>([])
  const legacyRepairInFlightRef = useRef(new Set<string>())
  const [memberProfile, setMemberProfile] = useState<WorkspaceMember | null>(null)
  const [locks, setLocks] = useState<DocumentLock[]>([])
  const [workspaceBalanceCredits, setWorkspaceBalanceCredits] = useState(0)
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null)
  const [isCanvasMaximized, setIsCanvasMaximized] = useState<boolean>(false)
  const [activeCanvasNodeId, setActiveCanvasNodeId] = useState<string | null>(null)
  const [viewportState, setViewportState] = useState<CanvasViewportState>({ panX: 0, panY: 0, zoomScale: 1 })
  const [isCinematicModeActive, setIsCinematicModeActive] = useState<boolean>(false)
  const [isCalendarShelfOpen, setIsCalendarShelfOpen] = useState<boolean>(false)
  const [externalEventsArray, setExternalEventsArray] = useState<MockExternalCalendarEvent[]>(MOCK_EXTERNAL_EVENTS)
  const [markdownMetadata, setMarkdownMetadata] = useState<MarkdownSyncMetadata>({ repoPath: null, isSyncingInProgress: false })
  const [isGestureKnifeActive, setIsGestureKnifeActive] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const canvasTransform = useMemo(() => ({
    x: viewportState.panX,
    y: viewportState.panY,
    zoom: viewportState.zoomScale,
  }), [viewportState.panX, viewportState.panY, viewportState.zoomScale])

  const setCanvasTransform = useCallback((transform: { x: number; y: number; zoom: number }) => {
    setViewportState({
      panX: Number.isFinite(transform.x) ? transform.x : 0,
      panY: Number.isFinite(transform.y) ? transform.y : 0,
      zoomScale: Number.isFinite(transform.zoom) && transform.zoom > 0 ? transform.zoom : 1,
    })
  }, [])


  const applyStripboardDelta = useCallback((delta: StripboardDelta): Promise<StripboardDeltaResult> => {
    if (isPublicReadOnly) return Promise.reject(new Error('Public timeline previews are read-only.'))
    if (isPreviewWorkspace) return Promise.reject(new Error('Preview workspaces are read-only.'))
    if (!currentUser) return Promise.reject(new Error('You must be signed in to update Stripboard records.'))
    return callStripboardDelta(delta)
  }, [currentUser, isPreviewWorkspace, isPublicReadOnly])

  const repairLegacyRecords = useCallback((recordRefs: Array<{ recordId: string; recordKind: StripboardRecordKind }>) => {
    if (!currentUser || isPublicReadOnly || isPreviewWorkspace || recordRefs.length === 0) return
    const pending = recordRefs
      .filter((entry) => !legacyRepairInFlightRef.current.has(`${entry.recordKind}:${entry.recordId}`))
      .slice(0, 50)
    if (!pending.length) return
    pending.forEach((entry) => legacyRepairInFlightRef.current.add(`${entry.recordKind}:${entry.recordId}`))
    void callStripboardDelta({
      operationId: createDeltaOperationId('lazy-migrate', effectiveWorkspaceId),
      operationType: 'MIGRATE_LEGACY',
      recordId: '',
      recordKind: 'GOAL',
      sourceStatus: null,
      destinationStatus: null,
      expectedVersion: 0,
      recordRefs: pending,
    }).catch((repairError) => {
      console.warn('[TimelineContext] Stripboard lazy repair failed', repairError)
    }).finally(() => {
      pending.forEach((entry) => legacyRepairInFlightRef.current.delete(`${entry.recordKind}:${entry.recordId}`))
    })
  }, [currentUser, effectiveWorkspaceId, isPreviewWorkspace, isPublicReadOnly])

  useEffect(() => {
    if (!effectiveWorkspaceId || !currentUser) {
      syncElementsRef.current = []
      syncChecklistItemsRef.current = []
      const timeout = window.setTimeout(() => {
        setElements([])
        setPathOnlyElementIds(new Set())
        setSelectedElementId(null)
        setIsCanvasMaximized(false)
        setActiveCanvasNodeId(null)
        setCanvasTransform({ x: 0, y: 0, zoom: 1 })
        setIsCinematicModeActive(false)
        setIsCalendarShelfOpen(false)
        setIsGestureKnifeActive(false)
        setIsLoading(false)
        setError(!effectiveWorkspaceId ? 'TimelineProvider requires an active workspaceId.' : null)
        dispatchFirstSnapshot()
      }, 0)
      return () => window.clearTimeout(timeout)
    }

    syncElementsRef.current = []
    syncChecklistItemsRef.current = []
    const resetTimeout = window.setTimeout(() => {
      setElements([])
      setPathOnlyElementIds(new Set())
      setSelectedElementId(null)
      setIsCanvasMaximized(false)
      setActiveCanvasNodeId(null)
      setCanvasTransform({ x: 0, y: 0, zoom: 1 })
      setIsCinematicModeActive(false)
      setIsCalendarShelfOpen(false)
      setIsGestureKnifeActive(false)
      setIsLoading(true)
      setError(null)
    }, 0)
    let didReceiveSnapshot = false

    const commitLoading = () => {
      if (!didReceiveSnapshot) window.clearTimeout(resetTimeout)
      didReceiveSnapshot = true
      dispatchFirstSnapshot()
    }

    if (!isAccessScopeReady) {
      return () => window.clearTimeout(resetTimeout)
    }

    if (isPreviewWorkspace) {
      commitLoading()
      if (isEduDemoWorkspace(effectiveWorkspaceId)) {
        let isCancelled = false
        const syncData = getEduDemoDatasetSync(effectiveWorkspaceId)
        if (syncData) {
          const previewElements = (syncData.elements ?? []).map((elementItem) => ({
            ...elementItem,
            workspaceId: effectiveWorkspaceId,
          }))
          syncElementsRef.current = previewElements
          setElements(previewElements)
          setError(null)
          setIsLoading(false)
          return () => {
            isCancelled = true
            window.clearTimeout(resetTimeout)
          }
        }
        getEduDemoDataset(effectiveWorkspaceId).then((data) => {
          if (isCancelled) return
          const previewElements = (data?.elements ?? []).map((elementItem) => ({
            ...elementItem,
            workspaceId: effectiveWorkspaceId,
          }))
          syncElementsRef.current = previewElements
          setElements(previewElements)
          setError(null)
          setIsLoading(false)
        }).catch((err) => {
          if (isCancelled) return
          setError(err instanceof Error ? err.message : String(err))
          setIsLoading(false)
        })
        return () => {
          isCancelled = true
          window.clearTimeout(resetTimeout)
        }
      }
      const seed = getExploreWorkspaceSeedByWorkspaceId(effectiveWorkspaceId)
      const previewElements = (seed?.elements ?? []).map((elementItem) => ({
        ...elementItem,
        workspaceId: effectiveWorkspaceId,
      }))
      syncElementsRef.current = previewElements
      setElements(previewElements)
      setError(null)
      setIsLoading(false)
      return () => window.clearTimeout(resetTimeout)
    }

    const plan = elementQueryPlan({
      isPublicReadOnly,
      appMode,
      isAdmin: Boolean(activeRole && isWorkspaceRole(activeRole) && roleCanPerform(activeRole, 'ADMIN')),
      restricted: isAccessRestricted,
      helper: isAccessHelper,
      workspaceId: effectiveWorkspaceId,
      uid: currentUser.uid,
    })

    const handleSnapshotDocs = (
      docs: Array<QueryDocumentSnapshot<DocumentData>>,
      pathOnlyIds: Set<string> = new Set(),
    ) => {
      commitLoading()
      const cleanupBatch = writeBatch(firestoreDb)
      let hasCleanupWrites = false
      const legacyRecords: Array<{ recordId: string; recordKind: StripboardRecordKind }> = []
      const updatedElements = docs.map((documentSnapshot) => {
        const data = documentSnapshot.data() as Partial<TimelineElement> & { dependencyIds?: unknown }
        if (!('boardStatus' in data) || !('recordVersion' in data) || !('priority' in data) || !('startDate' in data) || !('endDate' in data)) {
          legacyRecords.push({ recordId: documentSnapshot.id, recordKind: data.type ?? 'SUBTASK' })
        }
        if ('dependencyIds' in data) {
          cleanupBatch.update(documentSnapshot.ref, { dependencyIds: deleteField() })
          hasCleanupWrites = true
        }
        return normalizeElement(
          documentSnapshot.id,
          data,
          effectiveWorkspaceId,
        )
      })
      if (hasCleanupWrites) {
        cleanupBatch.commit().catch((cleanupError) => {
          console.warn('[TimelineContext] dependencyIds cleanup failed', cleanupError)
        })
      }
      syncElementsRef.current = updatedElements
      setElements(updatedElements)
      setPathOnlyElementIds(pathOnlyIds)
      setElementsLastSyncedAt(new Date())
      setElementsSubscriptionError(null)
      syncBridgeEngine.syncFirestoreToLocalCache(
        currentUser.uid,
        updatedElements.map(toSyncCanvasElement),
        getBridgeChecklistItems(updatedElements, syncChecklistItemsRef.current),
        isHelperSyncSession(userProfile?.role),
      )
      setError(null)
      setIsLoading(false)
      repairLegacyRecords(legacyRecords)
    }

    const handleSnapshotError = (snapshotError: Error) => {
      commitLoading()
      setError(snapshotError.message)
      setElementsSubscriptionError(snapshotError)
      setIsLoading(false)
    }

    if (plan === 'scoped') {
      const collector = createScopedElementsCollector<QueryDocumentSnapshot<DocumentData>>(({ docs, pathOnlyIds }) => {
        handleSnapshotDocs(docs, pathOnlyIds)
      })

      const unsubRead = onSnapshot(
        query(
          collection(firestoreDb, 'elements'),
          where('workspaceId', '==', effectiveWorkspaceId),
          where('scopeRead', 'array-contains', currentUser.uid),
        ),
        (snap) => {
          collector.setRead(snap.docs)
        },
        handleSnapshotError,
      )

      const unsubPath = onSnapshot(
        query(
          collection(firestoreDb, 'elements'),
          where('workspaceId', '==', effectiveWorkspaceId),
          where('scopePath', 'array-contains', currentUser.uid),
        ),
        (snap) => {
          collector.setPath(snap.docs)
        },
        handleSnapshotError,
      )

      return () => {
        window.clearTimeout(resetTimeout)
        unsubRead()
        unsubPath()
      }
    }

    const elementsQuery = plan === 'workspace'
      ? query(collection(firestoreDb, 'elements'), where('workspaceId', '==', effectiveWorkspaceId))
      : plan === 'helper'
      ? query(
          collection(firestoreDb, 'elements'),
          where('workspaceId', '==', effectiveWorkspaceId),
          where('helperRead', 'array-contains', currentUser.uid),
        )
      : query(
          collection(firestoreDb, 'elements'),
          where('workspaceId', '==', effectiveWorkspaceId),
          where('readAccess', 'array-contains', currentUser.uid),
        )

    const unsubscribe = onSnapshot(
      elementsQuery,
      (snapshot) => {
        handleSnapshotDocs(snapshot.docs, new Set())
      },
      handleSnapshotError,
    )

    return () => {
      window.clearTimeout(resetTimeout)
      unsubscribe()
    }
  }, [activeRole, appMode, currentUser, dispatchFirstSnapshot, effectiveWorkspaceId, isAccessHelper, isAccessRestricted, isAccessScopeReady, isPreviewWorkspace, isPublicReadOnly, refreshNonce, refreshToken, repairLegacyRecords, setCanvasTransform, userProfile?.role])

  useEffect(() => {
    syncChecklistItemsRef.current = []
    const resetTimeout = window.setTimeout(() => setChecklistItems([]), 0)
    if (!effectiveWorkspaceId || !currentUser) return () => window.clearTimeout(resetTimeout)

    if (isPreviewWorkspace) {
      const seed = getExploreWorkspaceSeedByWorkspaceId(effectiveWorkspaceId)
      const previewChecklistItems = (seed?.checklistItems ?? []).map((item) => ({
        ...item,
        workspaceId: effectiveWorkspaceId,
      }))
      syncChecklistItemsRef.current = previewChecklistItems
      setChecklistItems(previewChecklistItems)
      syncBridgeEngine.syncFirestoreToLocalCache(
        currentUser.uid,
        syncElementsRef.current.map(toSyncCanvasElement),
        getBridgeChecklistItems(syncElementsRef.current, previewChecklistItems),
        isHelperSyncSession(userProfile?.role),
      )
      return () => window.clearTimeout(resetTimeout)
    }

    // The book-model subscription (below) is the sole source of truth for
    // `checklistItems`. It fetches definitions + today's page + layout and
    // projects them; downstream consumers see the merged view via
    // `resolvedChecklistItems`. Nothing else needs to run here.
    return () => window.clearTimeout(resetTimeout)
  }, [currentUser, effectiveWorkspaceId, isPreviewWorkspace, refreshToken, repairLegacyRecords, userProfile?.role])

  // --- Book-model subscription ---------------------------------------------
  // Subscribes to the three-book model in parallel with the legacy
  // `checklist_items` subscription above. Projects the (definition, page,
  // layoutVersion) triples into the FlatChecklistItem shape via
  // checklistViewAdapter, then merges into `bookModelChecklistItems` state.
  // Merged view lives in `resolvedChecklistItems` (book-model wins on id).
  //
  // Midnight-rollover handling: page subscriptions are bound to
  // `pageKeyFor(type, new Date())` at setup. A session left open past
  // midnight would otherwise keep listening to yesterday's page. We poll
  // every 60s and, if any type's pageKey changed since the last check,
  // tear down JUST the page listeners (definitions unaffected) and
  // rebind them to the new keys. We also clear the per-type page cache
  // for that type so the new key's page starts fresh.
  useEffect(() => {
    setChecklistsLoaded(false)
    setBookModelChecklistItems([])
    if (!effectiveWorkspaceId || !currentUser || isPreviewWorkspace || !isAccessScopeReady) return
    if (isHelperAccount && !isAccessHelper) return

    const CHECKLIST_TYPES: ChecklistType[] = ['INDIVIDUAL', 'DAILY', 'WEEKLY']
    const workspaceTimeZone = workspaces?.find((w) => w.id === effectiveWorkspaceId)?.timezone

    let currentDefinitions: ChecklistDefinition[] = []
    const perTypePages: BookCurrentPageMap = emptyBookCurrentPageMap()
    const layoutCache: Record<string, ChecklistLayoutVersion | null> = {}
    const detailCache: Record<string, ChecklistDetailVersion | null> = {}
    let disposed = false

    const recompute = () => {
      if (disposed) return
      const projected = projectDefinitionsToChecklistItems(currentDefinitions, perTypePages)
      setBookModelChecklistItems(projected)
    }

    // Definitions subscription — the full set of checklists in the workspace.
    // Independent of the day; never torn down at midnight.
    const defsPlan = elementQueryPlan({
      isPublicReadOnly,
      appMode,
      isAdmin: Boolean(activeRole && isWorkspaceRole(activeRole) && roleCanPerform(activeRole, 'ADMIN')),
      restricted: isAccessRestricted,
      helper: isAccessHelper,
      workspaceId: effectiveWorkspaceId,
      uid: currentUser.uid,
    })

    const defsQuery = defsPlan === 'scoped'
      ? query(
          collection(firestoreDb, CHECKLISTS_COL),
          where('workspaceId', '==', effectiveWorkspaceId),
          where('scopeRead', 'array-contains', currentUser.uid),
        )
      : defsPlan === 'helper'
      ? query(
          collection(firestoreDb, CHECKLISTS_COL),
          where('workspaceId', '==', effectiveWorkspaceId),
          where('helperRead', 'array-contains', currentUser.uid),
        )
      : query(
          collection(firestoreDb, CHECKLISTS_COL),
          where('workspaceId', '==', effectiveWorkspaceId),
        )

    const defsUnsub = onSnapshot(
      defsQuery,
      (snap) => {
        setChecklistsLoaded(true)
        currentDefinitions = snap.docs
          .map((d) => d.data() as ChecklistDefinition)
          .filter((def) => !def.isArchived)
        recompute()
      },
      (err) => {
        setChecklistsLoaded(true)
        console.warn('[TimelineContext] checklists (definitions) listener failed', err)
      },
    )

    // Per-type page subscriptions, held in a map so we can rebind an
    // individual type when its pageKey rolls over.
    const pageUnsubsByType: Partial<Record<ChecklistType, () => void>> = {}
    const currentPageKeys: Partial<Record<ChecklistType, string>> = {}

    const subscribePageForType = (type: ChecklistType, pageKey: string) => {
      const bookId = bookIdFor(effectiveWorkspaceId, type)
      currentPageKeys[type] = pageKey
      return onSnapshot(
        doc(firestoreDb, `checklist_books/${bookId}/pages/${pageKey}`),
        async (pageSnap) => {
          const page = pageSnap.exists() ? (pageSnap.data() as ChecklistBookPage) : null
          if (!page) {
            perTypePages[type] = null
            recompute()
            return
          }
          const isHelperWrapper = getWrapperRole() === 'helper'
          const layoutCacheKey = `${bookId}::${page.layoutVersionId}`
          if (!isHelperWrapper && !(layoutCacheKey in layoutCache)) {
            layoutCache[layoutCacheKey] = await getLayoutVersion(bookId, page.layoutVersionId).catch((err) => {
              console.warn('[TimelineContext] layout version fetch failed', err)
              return null
            })
          }
          const detailVersionId = page.detailVersionId ?? null
          const detailCacheKey = detailVersionId ? `${bookId}::${detailVersionId}` : null
          if (!isHelperWrapper && detailVersionId && detailCacheKey && !(detailCacheKey in detailCache)) {
            detailCache[detailCacheKey] = await getDetailVersion(bookId, detailVersionId).catch((err) => {
              console.warn('[TimelineContext] detail version fetch failed', err)
              return null
            })
          }
          perTypePages[type] = {
            page,
            layoutVersion: layoutCache[layoutCacheKey] ?? null,
            detailVersion: (detailCacheKey ? detailCache[detailCacheKey] : null) ?? null,
          }
          recompute()
        },
        (err) => {
          console.warn(`[TimelineContext] book page listener (${type}) failed`, err)
        },
      )
    }

    // Initial bind — one subscription per type using today's pageKey in workspace time.
    CHECKLIST_TYPES.forEach((type) => {
      const pageKey = pageKeyFor(type, new Date(), workspaceTimeZone)
      pageUnsubsByType[type] = subscribePageForType(type, pageKey)
    })

    // Midnight-rollover watcher. INDIVIDUAL's pageKey is fixed at 'single'
    // so it will never trigger a rebind. DAILY and WEEKLY roll at
    // midnight workspace time.
    const rolloverInterval = window.setInterval(() => {
      if (disposed) return
      const now = new Date()
      CHECKLIST_TYPES.forEach((type) => {
        const nextKey = pageKeyFor(type, now, workspaceTimeZone)
        if (nextKey === currentPageKeys[type]) return
        pageUnsubsByType[type]?.()
        // Clear cached page for this type — the previous entry belonged
        // to yesterday's page and is no longer relevant.
        perTypePages[type] = null
        pageUnsubsByType[type] = subscribePageForType(type, nextKey)
      })
    }, 60_000)

    return () => {
      disposed = true
      window.clearInterval(rolloverInterval)
      defsUnsub()
      Object.values(pageUnsubsByType).forEach((u) => u?.())
    }
  }, [activeRole, appMode, currentUser, effectiveWorkspaceId, isAccessHelper, isAccessRestricted, isAccessScopeReady, isPreviewWorkspace, isPublicReadOnly, refreshToken, userProfile?.role])

  useEffect(() => {
    if (!effectiveWorkspaceId || !currentUser || isPublicReadOnly) return
    if (isPreviewWorkspace) {
      const normalizedEmail = currentUser.email?.trim().toLowerCase() ?? 'preview@example.test'
      setMemberProfile({
        id: `${effectiveWorkspaceId}_${currentUser.uid}`,
        workspaceId: effectiveWorkspaceId,
        workspaceSlug: activeWorkspaceSlug,
        uid: currentUser.uid,
        userId: currentUser.uid,
        user_email: normalizedEmail,
        email: normalizedEmail,
        role: 'READONLY',
        credits: 0,
        dailyCreditAllowance: 0,
        creditsConsumedToday: 0,
        status: 'ACTIVE',
      })
      return
    }
    if (isHelperAccount) {
      setMemberProfile(null)
      return
    }
    const resetTimeout = window.setTimeout(() => setMemberProfile(null), 0)

    const memberQuery = query(
      collection(firestoreDb, 'workspace_members'),
      where('workspaceId', '==', effectiveWorkspaceId),
    )

    const unsubscribe = onSnapshot(
      memberQuery,
      (snapshot) => {
        const normalizedEmail = currentUser.email?.trim().toLowerCase() ?? ''
        const matchingDocument = snapshot.docs.find((snapshotDocument) => {
          const data = snapshotDocument.data() as Partial<WorkspaceMember>
          const candidateUid = typeof data.uid === 'string' ? data.uid : undefined
          const candidateUserId = typeof data.userId === 'string' ? data.userId : undefined
          const candidateEmail = typeof data.email === 'string'
            ? data.email.trim().toLowerCase()
            : typeof data.user_email === 'string'
              ? data.user_email.trim().toLowerCase()
              : ''

          return snapshotDocument.id === `${effectiveWorkspaceId}_${currentUser.uid}` ||
            candidateUid === currentUser.uid ||
            candidateUserId === currentUser.uid ||
            (Boolean(normalizedEmail) && candidateEmail === normalizedEmail)
        })

        setMemberProfile(
          matchingDocument
            ? normalizeMemberProfile(matchingDocument.id, matchingDocument.data() as Partial<WorkspaceMember>)
            : null,
        )
      },
      (snapshotError) => {
        console.warn('[TimelineContext] workspace member listener failed', snapshotError)
        setMemberProfile(null)
      },
    )
    return () => {
      window.clearTimeout(resetTimeout)
      unsubscribe()
    }
  }, [activeWorkspaceSlug, currentUser, effectiveWorkspaceId, isPreviewWorkspace, isPublicReadOnly, userProfile?.role])

  useEffect(() => {
    if (!effectiveWorkspaceId || !currentUser || isPublicReadOnly || isPreviewWorkspace || isHelperAccount) return

    const locksQuery = query(
      collection(firestoreDb, 'locks'),
      where('workspaceId', '==', effectiveWorkspaceId),
    )

    const unsubscribe = onSnapshot(
      locksQuery,
      (snapshot) => {
        const now = Date.now()
        setLocks(
          snapshot.docs
            .map((snapshotDocument) =>
              normalizeLock(snapshotDocument.id, snapshotDocument.data() as Partial<DocumentLock>),
            )
            .filter((lock): lock is DocumentLock => lock !== null)
            .filter((lock) => lockExpiryMs(lock) > now),
        )
      },
      (snapshotError) => {
        console.warn('[TimelineContext] locks listener failed', snapshotError)
        setLocks([])
      },
    )
    return () => {
      unsubscribe()
    }
  }, [currentUser, effectiveWorkspaceId, isHelperAccount, isPreviewWorkspace, isPublicReadOnly])

  useEffect(() => {
    const localBalance = activeWorkspace?.teamBalanceCredits ?? activeWorkspace?.credits ?? 0
    const resetTimeout = window.setTimeout(() => setWorkspaceBalanceCredits(localBalance), 0)

    if (!effectiveWorkspaceId || isPreviewWorkspace || isHelperAccount) {
      return () => window.clearTimeout(resetTimeout)
    }

    const unsubscribe = onSnapshot(
      doc(firestoreDb, 'workspace_wallet', effectiveWorkspaceId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setWorkspaceBalanceCredits(localBalance)
          return
        }

        const data = snapshot.data()
        setWorkspaceBalanceCredits(typeof data.availableCredits === 'number' ? data.availableCredits : localBalance)
      },
      () => setWorkspaceBalanceCredits(localBalance),
    )
    return () => {
      window.clearTimeout(resetTimeout)
      unsubscribe()
    }
  }, [activeWorkspace?.credits, activeWorkspace?.teamBalanceCredits, effectiveWorkspaceId, isPreviewWorkspace, userProfile?.role])

  const childrenByParentId = useMemo(() => {
    const lookup = new Map<string, TimelineElement[]>()

    for (const element of elements) {
      if (!element.parentId) continue

      const siblings = lookup.get(element.parentId) ?? []
      siblings.push(element)
      lookup.set(element.parentId, siblings)
    }

    return lookup
  }, [elements])

  const elementById = useMemo(
    () => new Map(elements.map((element) => [element.id, element])),
    [elements],
  )

  // Merge the two projection sources — book-model wins on id conflict so a
  // freshly-created book-model checklist supersedes any lingering legacy
  // singleton for the same id during migration.
  const resolvedChecklistItems = useMemo<FlatChecklistItem[]>(() => {
    if (bookModelChecklistItems.length === 0) return checklistItems
    const seen = new Set(bookModelChecklistItems.map((item) => item.id))
    const legacyRemainder = checklistItems.filter((item) => !seen.has(item.id))
    return [...legacyRemainder, ...bookModelChecklistItems]
  }, [bookModelChecklistItems, checklistItems])

  const canPerformOnNode = useCallback(
    (action: WorkspaceAction, nodeId: string | null): boolean => {
      return resolveCanPerformOnNode({
        action,
        nodeId,
        canPerform,
        isAccessRestricted,
        uid: currentUser?.uid,
        pathOnlyElementIds,
        elementById,
        resolvedChecklistItems,
      })
    },
    [canPerform, currentUser?.uid, elementById, isAccessRestricted, pathOnlyElementIds, resolvedChecklistItems],
  )

  const planRouteIdentity = useCallback(async (
    input: {
      itemId: string
      type: TimelineElementType
      board: TimelineBoardKey
      parentId: string | null
      parentRoutePath: string
      existingRoutePath?: string
    },
    transaction: Transaction,
  ) => {
    const normalizedParentRoutePath = normalizeRoutePath(input.parentRoutePath)
    const signature = placementSignature(effectiveWorkspaceId, input.board, normalizedParentRoutePath, input.type)
    const historyRef = doc(firestoreDb, 'public_id_history', `${input.itemId}__${signature}`)
    const historySnapshot = await transaction.get(historyRef)
    let ordinal = typeof historySnapshot.data()?.ordinal === 'number' ? historySnapshot.data()?.ordinal as number : 0

    let isNewOrdinal = false
    let counterRef: ReturnType<typeof doc> | null = null
    if (!ordinal) {
      isNewOrdinal = true
      counterRef = doc(firestoreDb, 'public_id_counters', signature)
      const counterSnapshot = await transaction.get(counterRef)
      ordinal = (typeof counterSnapshot.data()?.next === 'number' ? counterSnapshot.data()?.next as number : 1)
    }

    const segment = segmentForOrdinal(input.type === 'GOAL' ? 'g' : input.type === 'TASK' ? 't' : 's', ordinal)
    const routePath = normalizedParentRoutePath ? `${normalizedParentRoutePath}/${segment}` : segment
    const identity = {
      routePath,
      parentRoutePath: normalizedParentRoutePath,
      publicId: buildDisplayId(activeWorkspaceSlug, input.board, routePath),
    }

    return {
      identity,
      apply: () => {
        if (isNewOrdinal && counterRef) {
          transaction.set(counterRef, {
            workspaceId: effectiveWorkspaceId,
            board: input.board,
            parentRoutePath: normalizedParentRoutePath,
            type: input.type,
            next: ordinal + 1,
            updatedAt: serverTimestamp(),
          }, { merge: true })
          transaction.set(historyRef, {
            itemId: input.itemId,
            workspaceId: effectiveWorkspaceId,
            board: input.board,
            parentId: input.parentId,
            parentRoutePath: normalizedParentRoutePath,
            type: input.type,
            ordinal,
            createdAt: serverTimestamp(),
            lastUsedAt: serverTimestamp(),
          }, { merge: true })
        } else {
          transaction.set(historyRef, {
            parentId: input.parentId,
            lastUsedAt: serverTimestamp(),
          }, { merge: true })
        }
      },
    }
  }, [activeWorkspaceSlug, effectiveWorkspaceId])

  const allocateRouteIdentity = useCallback(async (
    input: {
      itemId: string
      type: TimelineElementType
      board: TimelineBoardKey
      parentId: string | null
      parentRoutePath: string
      existingRoutePath?: string
    },
    transaction: Transaction,
  ) => {
    const planned = await planRouteIdentity(input, transaction)
    planned.apply()
    return planned.identity
  }, [planRouteIdentity])

  const calculateElementProgress = useCallback(
    (elementId: string) => {
      const target = elementById.get(elementId)
      return Math.round(Math.min(100, Math.max(0, target?.progress ?? 0)))
    },
    [elementById],
  )

  const getDefaultAccessIds = useCallback(async () => {
    if (!currentUser) throw new Error('You must be signed in to create timeline elements.')

    const memberSnapshot = await getDocs(
      query(
        collection(firestoreDb, 'workspace_members'),
        where('workspaceId', '==', effectiveWorkspaceId),
      ),
    )
    const activeMemberIds = memberSnapshot.docs
      .map((snapshot) => {
        const data = snapshot.data()
        if (data.status === 'PENDING' || data.status === 'DECLINED' || data.status === 'REVOKED') return null
        return typeof data.uid === 'string' && data.uid
          ? data.uid
          : data.userId
      })
      .filter((userId): userId is string => typeof userId === 'string')

    return uniqueUserIds([currentUser.uid, ...activeMemberIds])
  }, [currentUser, effectiveWorkspaceId])

  const visibleLocks = useMemo(
    () => ((isPreviewWorkspace || isHelperAccount) ? EMPTY_LOCKS : locks),
    [isPreviewWorkspace, locks, isHelperAccount],
  )

  const getExternalLock = useCallback(
    (targetId: string) => {
      if (!currentUser) return null
      return visibleLocks.find((lock) => lock.id === targetId && lock.lockedBy !== currentUser.uid) ?? null
    },
    [currentUser, visibleLocks],
  )

  const verifyUserOperationClearance = useCallback(
    (actionType: 'WRITE' | 'AI', actionCost = 0) => {
      if (testingOverrideAllowsAllFeatures()) return true
      if (isPublicReadOnly) {
        console.warn('[TimelineContext] Operation blocked: public timeline previews are read-only.')
        return false
      }
      if (isPreviewWorkspace) {
        console.warn('[TimelineContext] Operation blocked: Explore preview workspaces are read-only.')
        return false
      }

      const resolvedRole = normalizeRole(memberProfile?.role ?? activeRole)
      if (!roleCanPerform(resolvedRole, 'EDITOR')) {
        console.warn(`[TimelineContext] ${actionType} blocked: current workspace role cannot mutate workspace data.`)
        return false
      }

      // Demo unlock (R35 PD2/PD13) lifts only the credit part below; read-only and role checks above still apply.
      if (actionType === 'AI' && appMode === 'TEAM' && resolvedRole === 'EDITOR' && !isPersonalWorkspace(activeWorkspace) && !isWorkspaceDemoUnlocked(activeWorkspace)) {
        const aiControl = memberProfile?.effectiveControls?.ai
        if (aiControl?.mode === 'OFF') {
          console.warn('[TimelineContext] AI blocked: AI is disabled for your account.')
          return false
        }

        const { allowance, consumed } = resolveMemberAiBudgetAndConsumption(memberProfile)

        if (consumed + actionCost > allowance) {
          console.warn('[TimelineContext] AI blocked: daily credit allowance exceeded.')
          return false
        }

        if (workspaceBalanceCredits < actionCost) {
          console.warn('[TimelineContext] AI blocked: workspace credit balance is insufficient.')
          return false
        }
      }

      return true
    },
    [activeRole, activeWorkspace, appMode, isPreviewWorkspace, isPublicReadOnly, memberProfile, workspaceBalanceCredits],
  )

  const creditTaskCompletionReward = useCallback(async (itemId: string) => {
    if (!currentUser || isPublicReadOnly || isPreviewWorkspace) return { credited: false, remaining: 0 }

    try {
      return await claimPersonalCreditReward('CHECKLIST_COMPLETION', {
        sourceId: itemId,
        workspaceId: isPersonalWorkspace(activeWorkspace) ? undefined : activeWorkspace?.id,
      })
    } catch (rewardError) {
      console.warn('[TimelineContext] task completion credit failed', rewardError)
      return { credited: false, remaining: 0 }
    }
  }, [activeWorkspace, currentUser, isPreviewWorkspace, isPublicReadOnly])

  const maximizeCanvasWorkspace = useCallback((nodeId?: string) => {
    setActiveCanvasNodeId(nodeId ?? null)
    if (nodeId && nodeId !== WORKSPACE_DEFAULT_CHECKLIST_ID) setSelectedElementId(nodeId)
    setIsCanvasMaximized(true)
  }, [])

  const closeCanvasWorkspace = useCallback(() => {
    setIsCanvasMaximized(false)
    setActiveCanvasNodeId(null)
    setIsCinematicModeActive(false)
    setIsCalendarShelfOpen(false)
    setIsGestureKnifeActive(false)
  }, [])

  const panCanvasToCoordinates = useCallback((targetX: number, targetY: number) => {
    setViewportState((current) => ({
      ...current,
      panX: Number.isFinite(targetX) ? targetX : current.panX,
      panY: Number.isFinite(targetY) ? targetY : current.panY,
      zoomScale: current.zoomScale || 1,
    }))
  }, [])

  const createElements = useCallback(async (newElements: CreateTimelineElementInput[]) => {
    if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
    if (isPreviewWorkspace) throw new Error('Preview workspaces are read-only.')
    if (newElements.some((el) => !canPerformOnNode('timeline.create', el.parentId ?? null))) {
      throw new Error('Your workspace role cannot create timeline items.')
    }
    if (!currentUser) throw new Error('You must be signed in to create timeline elements.')
    if (newElements.length === 0) return []
    if (!verifyUserOperationClearance('WRITE')) {
      throw new Error('You do not have write access to this workspace.')
    }

    // Pre-flight the Firestore `hasStripboardFields` rule (see firestore.rules)
    // — it requires startDate and endDate on every element. When Line drafts
    // an item without dates, stripUndefinedForFirestore removes the keys and
    // the write fails with a cryptic permission-denied. Catch it here with a
    // specific message that names the actual offenders.
    const undatedElements = newElements.filter((element) =>
      (element.type as string) !== 'CHECKLIST' && (!element.startDate || !element.endDate),
    )
    if (undatedElements.length > 0) {
      const preview = undatedElements
        .slice(0, 3)
        .map((element) => `"${element.title ?? element.id ?? 'untitled'}"`)
        .join(', ')
      const suffix = undatedElements.length > 3 ? ` and ${undatedElements.length - 3} more` : ''
      throw new Error(
        `Missing start or end dates on ${undatedElements.length} item(s): ${preview}${suffix}. Ask Line to include dates for every goal, task, and subtask.`,
      )
    }

    // Pre-flight limits room check from counter docs (all-or-nothing)
    const additionsByBoard = new Map<CountedBoard, { goals: number; tasks: number; subtasks: number }>()
    const additionsByParent = new Map<string, { tasks: number; subtasks: number }>()

    for (const element of newElements) {
      const nextBoard = normalizeBoardKey(element.board)
      if (nextBoard === 'VISIONBOARD' || nextBoard === 'DASHBOARD') {
        const boardCounts = additionsByBoard.get(nextBoard) ?? { goals: 0, tasks: 0, subtasks: 0 }
        if (element.type === 'GOAL') boardCounts.goals += 1
        if (element.type === 'TASK') boardCounts.tasks += 1
        if (element.type === 'SUBTASK') boardCounts.subtasks += 1
        additionsByBoard.set(nextBoard, boardCounts)
      }

      if (element.parentId && (element.type === 'TASK' || element.type === 'SUBTASK')) {
        const parentCounts = additionsByParent.get(element.parentId) ?? { tasks: 0, subtasks: 0 }
        if (element.type === 'TASK') parentCounts.tasks += 1
        if (element.type === 'SUBTASK') parentCounts.subtasks += 1
        additionsByParent.set(element.parentId, parentCounts)
      }
    }

    if (additionsByBoard.size > 0 || additionsByParent.size > 0) {
      const isUnlimited = isWorkspaceDemoUnlocked(activeWorkspace) || testingOverrideAllowsAllFeatures()
      if (!isUnlimited) {
        // Read effective limits doc
        const effectiveLimitsSnap = await getDoc(doc(firestoreDb, 'workspaceEffectiveLimits', effectiveWorkspaceId))
        const limitsData = effectiveLimitsSnap.exists() ? (effectiveLimitsSnap.data() as Record<string, unknown>) : null
        const boardSoftCaps = (limitsData?.boardSoftCaps ?? {}) as Record<string, Record<string, number>>
        const softCaps = (limitsData?.softCaps ?? {}) as Record<string, number>
        const fallbackLimits = activeWorkspace?.workspaceLimits

        // Check board limits
        for (const [targetBoard, additions] of additionsByBoard.entries()) {
          const counterSnap = await getDoc(doc(firestoreDb, 'workspaces', effectiveWorkspaceId, 'elementCounters', targetBoard))
          const counterData = counterSnap.exists() ? counterSnap.data() : {}
          const currentGoals = typeof counterData?.goals === 'number' ? counterData.goals : 0
          const currentTasks = typeof counterData?.tasks === 'number' ? counterData.tasks : 0
          const currentSubtasks = typeof counterData?.subtasks === 'number' ? counterData.subtasks : 0

          const goalLimit = boardSoftCaps[targetBoard]?.maxGoalsPerBoard ?? softCaps.maxGoalsPerBoard ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxGoalsPerBoard', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxGoalsPerBoard)
          if (goalLimit !== -1 && currentGoals + additions.goals > goalLimit) {
            const message = getNlsString('NLSBoardLimitGoalsReached', { limit: goalLimit })
            emitToast(message, 'error', {
              label: getNlsString('NLSChatActionUpgrade'),
              run: () => {
                window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
                  detail: { featureKey: CAP_KEY_TO_FEATURE_KEY.maxGoalsPerBoard, limit: goalLimit, currentUsage: currentGoals },
                }))
              },
            })
            throw new Error(message)
          }

          const taskLimit = boardSoftCaps[targetBoard]?.maxTasksPerBoard ?? softCaps.maxTasksPerBoard ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxTasksPerBoard', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxTasksPerBoard)
          if (taskLimit !== -1 && currentTasks + additions.tasks > taskLimit) {
            const message = getNlsString('NLSBoardLimitTasksReached', { limit: taskLimit })
            emitToast(message, 'error', {
              label: getNlsString('NLSChatActionUpgrade'),
              run: () => {
                window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
                  detail: { featureKey: CAP_KEY_TO_FEATURE_KEY.maxTasksPerBoard, limit: taskLimit, currentUsage: currentTasks },
                }))
              },
            })
            throw new Error(message)
          }

          const subtaskLimit = boardSoftCaps[targetBoard]?.maxSubtasksPerBoard ?? softCaps.maxSubtasksPerBoard ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxSubtasksPerBoard', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxSubtasksPerBoard)
          if (subtaskLimit !== -1 && currentSubtasks + additions.subtasks > subtaskLimit) {
            const message = getNlsString('NLSBoardLimitSubtasksReached', { limit: subtaskLimit })
            emitToast(message, 'error', {
              label: getNlsString('NLSChatActionUpgrade'),
              run: () => {
                window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
                  detail: { featureKey: CAP_KEY_TO_FEATURE_KEY.maxSubtasksPerBoard, limit: subtaskLimit, currentUsage: currentSubtasks },
                }))
              },
            })
            throw new Error(message)
          }
        }

        // Check parent limits
        for (const [parentId, additions] of additionsByParent.entries()) {
          const pCounterSnap = await getDoc(doc(firestoreDb, 'workspaces', effectiveWorkspaceId, 'elementParentCounters', parentId))
          const pCounterData = pCounterSnap.exists() ? pCounterSnap.data() : {}
          const currentPTasks = typeof pCounterData?.tasks === 'number' ? pCounterData.tasks : 0
          const currentPSubtasks = typeof pCounterData?.subtasks === 'number' ? pCounterData.subtasks : 0

          if (additions.tasks > 0) {
            const taskLimit = softCaps.maxTasksPerGoal ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxTasksPerGoal', 'DASHBOARD') : WORKSPACE_COUNT_HARD_CAPS.maxTasksPerGoal)
            if (taskLimit !== -1 && currentPTasks + additions.tasks > taskLimit) {
              const message = getNlsString('NLSParentLimitTasksReached', { limit: taskLimit })
              emitToast(message, 'error', {
                label: getNlsString('NLSChatActionUpgrade'),
                run: () => {
                  window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
                    detail: { featureKey: CAP_KEY_TO_FEATURE_KEY.maxTasksPerGoal, limit: taskLimit, currentUsage: currentPTasks },
                  }))
                },
              })
              throw new Error(message)
            }
          }

          if (additions.subtasks > 0) {
            const subtaskLimit = softCaps.maxSubtasksPerTask ?? (fallbackLimits ? getWorkspaceLimitValue(fallbackLimits, 'maxSubtasksPerTask', 'DASHBOARD') : WORKSPACE_COUNT_HARD_CAPS.maxSubtasksPerTask)
            if (subtaskLimit !== -1 && currentPSubtasks + additions.subtasks > subtaskLimit) {
              const message = getNlsString('NLSParentLimitSubtasksReached', { limit: subtaskLimit })
              emitToast(message, 'error', {
                label: getNlsString('NLSChatActionUpgrade'),
                run: () => {
                  window.dispatchEvent(new CustomEvent('linkhub:paywall-required', {
                    detail: { featureKey: CAP_KEY_TO_FEATURE_KEY.maxSubtasksPerTask, limit: subtaskLimit, currentUsage: currentPSubtasks },
                  }))
                },
              })
              throw new Error(message)
            }
          }
        }
      }
    }

    const elementsCollection = collection(firestoreDb, 'elements')
    const idTranslation = new Map<string, string>()

    for (const element of newElements) {
      if (element.id) idTranslation.set(element.id, doc(elementsCollection).id)
    }

    const accessIds = await getDefaultAccessIds()

    const savedElements: TimelineElement[] = []
    const savedElementByOriginalId = new Map<string, TimelineElement>()

    try {
      for (const element of newElements) {
        const elementRef = element.id
          ? doc(elementsCollection, idTranslation.get(element.id) ?? doc(elementsCollection).id)
          : doc(elementsCollection)
        const parentId = element.parentId && idTranslation.has(element.parentId)
          ? idTranslation.get(element.parentId) ?? null
          : element.parentId
        const parentElement = parentId
          ? savedElementByOriginalId.get(element.parentId ?? '') ?? elementById.get(parentId) ?? null
          : null
        const board = normalizeBoardKey(element.board ?? parentElement?.board)
        const routeIdentity = await runTransaction(firestoreDb, async (transaction) => {
          // --- READ PHASE ---
          const routePlan = await planRouteIdentity({
            itemId: elementRef.id,
            type: element.type,
            board,
            parentId,
            parentRoutePath: parentElement?.routePath ?? '',
          }, transaction)

          const savedElement: TimelineElement & { createdAt?: unknown } = {
            ...element,
            id: elementRef.id,
            parentId,
            board,
            workspaceId: effectiveWorkspaceId,
            publicId: routePlan.identity.publicId,
            routePath: routePlan.identity.routePath,
            parentRoutePath: routePlan.identity.parentRoutePath,
            boardStatus: element.boardStatus ?? (element.startDate ? 'TODO' : null),
            priority: element.priority ?? 1,
            dependencyNodeIds: element.dependencyNodeIds ?? [],
            invalidDependencyNodeIds: element.invalidDependencyNodeIds ?? [],
            recordVersion: element.recordVersion ?? 1,
            statusSyncOrigin: element.statusSyncOrigin ?? 'CREATE',
            statusSyncVersion: element.statusSyncVersion ?? 1,
            checklist: normalizeChecklistItems(element.checklist),
            readAccess: accessIds,
            writeAccess: accessIds,
            directReadAccess: accessIds,
            directWriteAccess: accessIds,
            lastUpdatedBy: currentUser.uid,
            createdAt: serverTimestamp(),
            lastUpdatedAt: serverTimestamp(),
          }

          let boardCounterWrite: (() => void) | null = null
          let parentCounterWrite: (() => void) | null = null

          if (isCountedElement(savedElement)) {
            const boardCounterRef = doc(firestoreDb, 'workspaces', effectiveWorkspaceId, 'elementCounters', board)
            const boardSnap = await transaction.get(boardCounterRef)
            const bData = boardSnap.data() ?? {}
            const field = element.type === 'GOAL' ? 'goals' : element.type === 'TASK' ? 'tasks' : 'subtasks'
            const nextGoals = typeof bData.goals === 'number' ? bData.goals + (field === 'goals' ? 1 : 0) : field === 'goals' ? 1 : 0
            const nextTasks = typeof bData.tasks === 'number' ? bData.tasks + (field === 'tasks' ? 1 : 0) : field === 'tasks' ? 1 : 0
            const nextSubtasks = typeof bData.subtasks === 'number' ? bData.subtasks + (field === 'subtasks' ? 1 : 0) : field === 'subtasks' ? 1 : 0

            boardCounterWrite = () => {
              transaction.set(boardCounterRef, {
                goals: nextGoals,
                tasks: nextTasks,
                subtasks: nextSubtasks,
                lastElementId: elementRef.id,
                updatedAt: serverTimestamp(),
              }, { merge: true })
            }

            if (parentId && !isSyntheticParentId(parentId) && (element.type === 'TASK' || element.type === 'SUBTASK')) {
              const parentCounterRef = doc(firestoreDb, 'workspaces', effectiveWorkspaceId, 'elementParentCounters', parentId)
              const parentSnap = await transaction.get(parentCounterRef)
              const pData = parentSnap.data() ?? {}
              const pField = element.type === 'TASK' ? 'tasks' : 'subtasks'
              const nextPTasks = typeof pData.tasks === 'number' ? pData.tasks + (pField === 'tasks' ? 1 : 0) : pField === 'tasks' ? 1 : 0
              const nextPSubtasks = typeof pData.subtasks === 'number' ? pData.subtasks + (pField === 'subtasks' ? 1 : 0) : pField === 'subtasks' ? 1 : 0

              parentCounterWrite = () => {
                transaction.set(parentCounterRef, {
                  tasks: nextPTasks,
                  subtasks: nextPSubtasks,
                  lastElementId: elementRef.id,
                  updatedAt: serverTimestamp(),
                }, { merge: true })
              }
            }
          }

          // --- WRITE PHASE ---
          routePlan.apply()
          if (boardCounterWrite) boardCounterWrite()
          if (parentCounterWrite) parentCounterWrite()
          transaction.set(elementRef, stripUndefinedForFirestore(savedElement))
          return savedElement
        })

        savedElements.push(routeIdentity)
        if (element.id) savedElementByOriginalId.set(element.id, routeIdentity)
      }
    } catch (createError) {
      if (savedElements.length > 0) {
        await binElements(effectiveWorkspaceId, savedElements.map((s) => s.id)).catch(() => {})
      }
      throw createError
    }

    // Checklist writes run per-element in parallel. Wrapped in a per-element
    // catch so ONE bad element's checklist sync never sinks Move-to-Manage —
    // containers are already durably saved above via the runTransaction loop.
    // A checklist-side failure downgrades to a console warning; the container
    // is visible in Manage without its checklists (rebuildable from the AI
    // draft or a manual re-sync) instead of the whole navigation being
    // blocked by an error toast. Per the user's "Move-to-Manage must not
    // error, no matter what" requirement, this is the last line of defence
    // behind the deterministic checklist gate in aiService + the
    // checklistType fix in syncChecklistBoardItems.
    await Promise.all(savedElements.map((savedElement) =>
      syncChecklistBoardItems(
        savedElement.id,
        effectiveWorkspaceId,
        activeWorkspaceSlug,
        savedElement.board,
        savedElement.routePath ?? '',
        savedElement.checklist,
        { id: currentUser.uid, name: currentDisplayName || currentUser.email || 'Workspace admin' },
        { startDate: savedElement.startDate, endDate: savedElement.endDate },
        { restricted: isAccessRestricted, uid: currentUser.uid },
      ).catch((error) => {
        // Elevated from warn → error so a genuine visibility bug is loud in
        // DevTools instead of scrolling past. The container is already saved,
        // so we deliberately do NOT throw or block Move-to-Manage — the goal
        // is diagnosability, not aborting the handoff.
        console.error('[createElements] checklist sync failed for element — container saved, checklists degraded', {
          elementId: savedElement.id,
          checklistCount: savedElement.checklist?.length ?? 0,
          checklistItemIds: (savedElement.checklist ?? []).map((c) => c.id),
          error: (error as Error)?.message,
          stack: (error as Error)?.stack,
        })
      }),
    ))
    await Promise.all(savedElements.map((savedElement) =>
      recordWorkspaceActivity(firestoreDb, {
        workspaceId: effectiveWorkspaceId,
        actorId: currentUser.uid,
        actorName: currentDisplayName || currentUser.email,
        targetType: savedElement.type,
        targetId: savedElement.id,
        targetTitle: savedElement.title,
        action: 'CREATE',
        summary: `Created ${savedElement.title}`,
        board: savedElement.board,
      }).catch((error) => console.warn('[TimelineContext] activity write failed', error)),
    ))
    savedElements.forEach((savedElement) => {
      const action = savedElement.type === 'GOAL'
        ? 'TRACK_CREATE_GOAL'
        : savedElement.type === 'TASK'
          ? 'TRACK_CREATE_TASK'
          : 'TRACK_CREATE_SUB'
      emitExperienceEvent(action, {
        workspaceId: effectiveWorkspaceId,
        sourceId: savedElement.id,
        increment: 1,
      })
    })
    return savedElements
  }, [
    activeWorkspace?.workspaceLimits,
    activeWorkspaceSlug,
    allocateRouteIdentity,
    canPerformOnNode,
    currentUser,
    effectiveWorkspaceId,
    elementById,
    isAccessRestricted,
    elements,
    getDefaultAccessIds,
    isPublicReadOnly,
    verifyUserOperationClearance,
  ])

  const createElement = useCallback(async (element: CreateTimelineElementInput) => {
    const [savedElement] = await createElements([element])
    return savedElement
  }, [createElements])

  const updateElement = useCallback(async (elementId: string, updates: Partial<TimelineElement>) => {
    if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
    const isMove = 'board' in updates || 'parentId' in updates || 'deletedAt' in updates || 'deletedFromBoard' in updates
    if (!canPerformOnNode(isMove ? 'timeline.move_restore' : 'timeline.edit', elementId)) throw new Error('Your workspace role cannot update this timeline item.')
    if (!currentUser) throw new Error('You must be signed in to update timeline elements.')
    const target = elementById.get(elementId)
    const externalLock = getExternalLock(elementId)

    if (!verifyUserOperationClearance('WRITE')) {
      throw new Error('You do not have write access to this workspace.')
    }

    if (externalLock) {
      throw new Error(`This item is locked by ${externalLock.lockedByName}.`)
    }

    if (appMode === 'TEAM' && !roleCanPerform(activeRole, 'EDITOR')) {
      throw new Error('You do not have write access to this timeline item.')
    }

    if (appMode !== 'TEAM' && !roleCanPerform(activeRole, 'ADMIN') && target && !target.writeAccess.includes(currentUser.uid)) {
      throw new Error('You do not have write access to this timeline item.')
    }

    if (!target) throw new Error('Timeline item is no longer available.')
    let deltaVersion = target.recordVersion
    let deltaStatus = target.boardStatus
    const runProtectedDelta = async (delta: StripboardDelta) => {
      const result = await applyStripboardDelta(delta)
      deltaVersion = result.recordVersion
      deltaStatus = result.boardStatus
    }
    if ('dependencyNodeIds' in updates && updates.dependencyNodeIds) {
      await runProtectedDelta({
        operationId: createDeltaOperationId('element-dependencies', elementId),
        operationType: 'DEPENDENCY_UPDATE',
        recordId: elementId,
        recordKind: target.type,
        sourceStatus: deltaStatus,
        destinationStatus: deltaStatus,
        expectedVersion: deltaVersion,
        dependencyNodeIds: updates.dependencyNodeIds,
      })
    }
    if ('priority' in updates && typeof updates.priority === 'number') {
      await runProtectedDelta({
        operationId: createDeltaOperationId('element-priority', elementId),
        operationType: 'PRIORITY_UPDATE',
        recordId: elementId,
        recordKind: target.type,
        sourceStatus: deltaStatus,
        destinationStatus: deltaStatus,
        expectedVersion: deltaVersion,
        priority: updates.priority,
      })
    }
    if ('startDate' in updates || 'endDate' in updates) {
      const nextStartDate = 'startDate' in updates ? updates.startDate ?? null : target.startDate ?? null
      const nextEndDate = 'endDate' in updates ? updates.endDate ?? null : target.endDate ?? null
      await runProtectedDelta({
        operationId: createDeltaOperationId('element-dates', elementId),
        operationType: 'DATE_UPDATE',
        recordId: elementId,
        recordKind: target.type,
        sourceStatus: deltaStatus,
        destinationStatus: deltaStatus,
        expectedVersion: deltaVersion,
        startDate: nextStartDate,
        endDate: nextEndDate,
      })
      // Parent-scoped recurring: mirror the new range onto every DAILY /
      // WEEKLY child so their `recurringWindow` tracks the parent. The
      // per-date filter in ChecklistDrawingBoard.displayNodes then
      // automatically hides instances whose date falls outside the new
      // range — old completion pages persist (auditable) but stop
      // rendering. Fire-and-forget so a follow-up mirror failure never
      // rolls back the parent date write.
      void mirrorRecurringWindowsForParent(elementId, target.workspaceId, nextStartDate, nextEndDate)
    }
    const requestedStatus = 'boardStatus' in updates
      ? updates.boardStatus
      : 'workflowStatus' in updates
        ? updates.workflowStatus
        : undefined
    if (requestedStatus !== undefined) {
      await runProtectedDelta({
        operationId: createDeltaOperationId('element-status', elementId),
        operationType: 'STATUS_MOVE',
        recordId: elementId,
        recordKind: target.type,
        sourceStatus: deltaStatus,
        destinationStatus: requestedStatus,
        expectedVersion: deltaVersion,
      })
    }

    const safeUpdates: Partial<TimelineElement> = { ...updates }
    delete safeUpdates.id
    delete safeUpdates.workspaceId
    delete safeUpdates.lastUpdatedAt
    delete safeUpdates.lastUpdatedBy
    delete safeUpdates.checklist
    delete safeUpdates.boardStatus
    delete safeUpdates.workflowStatus
    delete safeUpdates.priority
    delete safeUpdates.dependencyNodeIds
    delete safeUpdates.startDate
    delete safeUpdates.endDate
    delete safeUpdates.recordVersion
    delete safeUpdates.invalidDependencyNodeIds
    delete safeUpdates.statusSyncOrigin
    delete safeUpdates.statusSyncVersion
    const normalizedChecklist = updates.checklist ? normalizeChecklistItems(updates.checklist) : undefined
    const boardOrParentChanged = Boolean(target && (
      (safeUpdates.board && safeUpdates.board !== target.board) ||
      ('parentId' in safeUpdates && safeUpdates.parentId !== target.parentId)
    ))

    if (boardOrParentChanged) {
      const nextBoard = normalizeBoardKey(safeUpdates.board ?? target.board)
      const nextParentId = typeof safeUpdates.parentId === 'string'
        ? safeUpdates.parentId
        : safeUpdates.parentId === null
          ? null
          : target.parentId

      if (nextBoard === 'BIN') {
        await binElements(effectiveWorkspaceId, [elementId])
      } else if (target.board === 'BIN') {
        await restoreElements(effectiveWorkspaceId, [elementId], nextBoard, nextParentId)
      } else if (target.board !== nextBoard) {
        await moveElements(effectiveWorkspaceId, [elementId], nextBoard, nextParentId)
      } else if (target.parentId !== nextParentId) {
        await reparentElements(effectiveWorkspaceId, [elementId], nextParentId, nextBoard)
      }

      delete safeUpdates.board
      delete safeUpdates.parentId
      if (Object.keys(safeUpdates).length > 0 || normalizedChecklist) {
        await updateDoc(doc(firestoreDb, 'elements', elementId), stripUndefinedForFirestore({
          ...safeUpdates,
          ...(normalizedChecklist ? { checklist: normalizedChecklist } : {}),
          lastUpdatedBy: currentUser.uid,
          lastUpdatedAt: serverTimestamp(),
        }))
      }
    } else {
      delete safeUpdates.board
      delete safeUpdates.parentId
      if (Object.keys(safeUpdates).length > 0 || normalizedChecklist) {
        await updateDoc(doc(firestoreDb, 'elements', elementId), stripUndefinedForFirestore({
          ...safeUpdates,
          ...(normalizedChecklist ? { checklist: normalizedChecklist } : {}),
          lastUpdatedBy: currentUser.uid,
          lastUpdatedAt: serverTimestamp(),
        }))
      }
    }
    if (normalizedChecklist) {
      const nextStartDate = 'startDate' in updates ? updates.startDate ?? null : target.startDate ?? null
      const nextEndDate = 'endDate' in updates ? updates.endDate ?? null : target.endDate ?? null
      await syncChecklistBoardItems(
        elementId,
        target.workspaceId,
        activeWorkspaceSlug,
        normalizeBoardKey(safeUpdates.board ?? target.board),
        target.routePath ?? '',
        normalizedChecklist,
        { id: currentUser.uid, name: currentDisplayName || currentUser.email || 'Workspace admin' },
        { startDate: nextStartDate, endDate: nextEndDate },
        { restricted: isAccessRestricted, uid: currentUser.uid },
      )
    }
    if (Object.keys(safeUpdates).length > 0 || normalizedChecklist) {
      await recordWorkspaceActivity(firestoreDb, {
        workspaceId: target.workspaceId,
        actorId: currentUser.uid,
        actorName: currentDisplayName || currentUser.email,
        targetType: target.type,
        targetId: target.id,
        targetTitle: updates.title ?? target.title,
        action: updates.board && updates.board !== target.board ? 'MOVE_BOARD' : 'UPDATE',
        summary: updates.board && updates.board !== target.board
          ? `Moved ${target.title} to ${updates.board}`
          : `Updated ${updates.title ?? target.title}`,
        board: updates.board ?? target.board,
      }).catch((error) => console.warn('[TimelineContext] activity write failed', error))
    }
  }, [
    activeRole,
    activeWorkspaceSlug,
    allocateRouteIdentity,
    appMode,
    applyStripboardDelta,
    canPerformOnNode,
    currentUser,
    elementById,
    getExternalLock,
    isAccessRestricted,
    isPublicReadOnly,
    verifyUserOperationClearance,
  ])

  const updateChecklistItem = useCallback(async (checklistItemId: string, updates: Partial<FlatChecklistItem>) => {
    if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
    if (!currentUser) throw new Error('You must be signed in to update checklist items.')
    if (!canPerformOnNode('checklist.change_status', checklistItemId)) throw new Error('Your workspace role cannot change checklist status.')
    const target = resolvedChecklistItems.find((item) => item.id === checklistItemId)
    if (!target) throw new Error('Checklist item is no longer available.')

    if (!('workflowStatus' in updates)) throw new Error('Checklist status mutations require a workflowStatus value.')
    if ('verifiedCoordinates' in updates && !canPerformOnNode('checklist.location_checkin', checklistItemId)) {
      throw new Error('Your workspace role cannot submit a location check-in.')
    }
    await updateChecklistWorkflowStatus({
      item: target,
      workflowStatus: updates.workflowStatus,
      coordinates: updates.verifiedCoordinates ?? null,
      runDelta: applyStripboardDelta,
      actor: { id: currentUser.uid, name: currentDisplayName || currentUser.email || null },
    })
  }, [applyStripboardDelta, canPerformOnNode, currentDisplayName, resolvedChecklistItems, currentUser, isPublicReadOnly])

  const updateChecklistPrerequisites = useCallback(async (
    checklistItemId: string,
    prerequisiteIds: string[],
  ) => {
    if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
    if (!currentUser) throw new Error('You must be signed in to update checklist dependencies.')
    if (!canPerformOnNode('checklist.edit_canvas', checklistItemId) || !verifyUserOperationClearance('WRITE')) {
      throw new Error('You do not have permission to change checklist dependencies.')
    }
    const target = resolvedChecklistItems.find((item) => item.id === checklistItemId)
    if (!target) throw new Error('Checklist item is no longer available.')
    await updateChecklistDependencies({
      item: target,
      dependencyNodeIds: prerequisiteIds,
      runDelta: applyStripboardDelta,
      actor: { id: currentUser.uid, name: currentDisplayName || currentUser.email || null },
    })
  }, [applyStripboardDelta, canPerformOnNode, currentDisplayName, resolvedChecklistItems, currentUser, isPublicReadOnly, verifyUserOperationClearance])

  const deleteElement = useCallback(async (elementId: string) => {
    if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
    if (!canPerformOnNode('timeline.delete', elementId)) throw new Error('Your workspace role cannot delete timeline items.')
    const externalLock = getExternalLock(elementId)
    if (!verifyUserOperationClearance('WRITE')) {
      throw new Error('You do not have write access to this workspace.')
    }
    if (externalLock) {
      throw new Error(`This item is locked by ${externalLock.lockedByName}.`)
    }

    const descendants: TimelineElement[] = []
    const collectDescendants = (targetId: string) => {
      for (const child of childrenByParentId.get(targetId) ?? []) {
        descendants.push(child)
        collectDescendants(child.id)
      }
    }

    collectDescendants(elementId)

    const target = elementById.get(elementId)
    await binElements(effectiveWorkspaceId, [elementId])
    if (target) {
      await recordWorkspaceActivity(firestoreDb, {
        workspaceId: target.workspaceId,
        actorId: currentUser?.uid,
        actorName: currentDisplayName || currentUser?.email,
        targetType: target.type,
        targetId: target.id,
        targetTitle: target.title,
        action: 'DELETE',
        summary: `Moved ${target.title} to Bin`,
        board: 'BIN',
      }).catch((error) => console.warn('[TimelineContext] activity write failed', error))
    }

    setSelectedElementId((current) =>
      current === elementId || descendants.some((descendant) => descendant.id === current) ? null : current,
    )
  }, [canPerformOnNode, childrenByParentId, currentDisplayName, currentUser?.email, currentUser?.uid, effectiveWorkspaceId, elementById, getExternalLock, isPublicReadOnly, verifyUserOperationClearance])

  /**
   * Permanently remove an element (and all descendants) — used by Manage's
   * Delete button when the selection already lives in Bin. Unlike
   * `deleteElement`, which moves to Bin, this writes nothing back: the Firestore
   * docs are deleted outright.
   */
  const permanentlyDeleteElement = useCallback(async (elementId: string) => {
    if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
    if (!canPerform('board.delete_forever')) throw new Error('Your workspace role cannot permanently delete items.')
    if (!verifyUserOperationClearance('WRITE')) {
      throw new Error('You do not have write access to this workspace.')
    }
    const externalLock = getExternalLock(elementId)
    if (externalLock) {
      throw new Error(`This item is locked by ${externalLock.lockedByName}.`)
    }
    const root = elementById.get(elementId)
    if (!root) return

    const descendants: TimelineElement[] = []
    const collectDescendants = (targetId: string) => {
      for (const child of childrenByParentId.get(targetId) ?? []) {
        descendants.push(child)
        collectDescendants(child.id)
      }
    }
    collectDescendants(elementId)

    await deleteForeverElements(effectiveWorkspaceId, [elementId])

    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: root.workspaceId,
      actorId: currentUser?.uid,
      actorName: currentDisplayName || currentUser?.email,
      targetType: root.type,
      targetId: root.id,
      targetTitle: root.title,
      action: 'DELETE',
      summary: `Permanently deleted ${root.title}`,
      board: 'BIN',
    }).catch((error) => console.warn('[TimelineContext] activity write failed', error))

    setSelectedElementId((current) =>
      current === elementId || descendants.some((descendant) => descendant.id === current) ? null : current,
    )
  }, [canPerform, childrenByParentId, currentDisplayName, currentUser?.email, currentUser?.uid, effectiveWorkspaceId, elementById, getExternalLock, isPublicReadOnly, verifyUserOperationClearance])

  const moveFromBin = useCallback(async (
    elementId: string,
    targetBoard: TimelineBoardKey,
    newParentId: string | null,
  ) => {
    if (isPublicReadOnly) throw new Error('Public timeline previews are read-only.')
    if (!canPerformOnNode('timeline.move_restore', elementId)) throw new Error('Your workspace role cannot restore timeline items.')
    if (targetBoard === 'BIN') throw new Error('Cannot move an item from Bin into Bin.')
    if (!verifyUserOperationClearance('WRITE')) {
      throw new Error('You do not have write access to this workspace.')
    }
    const externalLock = getExternalLock(elementId)
    if (externalLock) {
      throw new Error(`This item is locked by ${externalLock.lockedByName}.`)
    }
    const root = elementById.get(elementId)
    if (!root) throw new Error('Item not found.')
    if (root.board !== 'BIN') throw new Error('Only items in Bin can be moved this way.')

    if (newParentId) {
      const parent = elementById.get(newParentId)
      if (!parent) throw new Error('Selected parent no longer exists.')
      if (parent.board !== targetBoard) throw new Error('Parent must live on the chosen board.')
      const expectedParentType =
        root.type === 'TASK' ? 'GOAL' : root.type === 'SUBTASK' ? 'TASK' : null
      if (!expectedParentType || parent.type !== expectedParentType) {
        throw new Error('Selected parent is not compatible with this item type.')
      }
    } else if (root.type !== 'GOAL') {
      throw new Error('Tasks and subtasks need a parent on the target board.')
    }

    await restoreElements(effectiveWorkspaceId, [elementId], targetBoard, newParentId)

    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: root.workspaceId,
      actorId: currentUser?.uid,
      actorName: currentDisplayName || currentUser?.email,
      targetType: root.type,
      targetId: root.id,
      targetTitle: root.title,
      action: 'MOVE_BOARD',
      summary: `Moved ${root.title} from Bin to ${targetBoard}`,
      board: targetBoard,
    }).catch((error) => console.warn('[TimelineContext] activity write failed', error))
  }, [canPerformOnNode, currentUser, effectiveWorkspaceId, elementById, getExternalLock, isPublicReadOnly, verifyUserOperationClearance])

  const value = useMemo<TimelineContextValue>(
    () => ({
      elements,
      pathOnlyElementIds,
      isAccessRestricted,
      elementsLastSyncedAt,
      elementsSubscriptionError,
      refreshElementsSubscription,
      checklistItems: resolvedChecklistItems,
      canPerformOnNode,
      workspaceId: effectiveWorkspaceId,
      memberProfile,
      locks: visibleLocks,
      viewportState,
      canvasTransform,
      isCinematicModeActive,
      isCalendarShelfOpen,
      externalEventsArray,
      markdownMetadata,
      isGestureKnifeActive,
      isPublicReadOnly,
      isLoading,
      checklistsLoaded,
      error,
      selectedElementId,
      isCanvasMaximized,
      activeCanvasNodeId,
      setViewportState,
      setCanvasTransform,
      setIsCinematicModeActive,
      setIsCalendarShelfOpen,
      setExternalEventsArray,
      setMarkdownMetadata,
      setIsGestureKnifeActive,
      setSelectedElementId,
      setIsCanvasMaximized,
      setActiveCanvasNodeId,
      maximizeCanvasWorkspace,
      closeCanvasWorkspace,
      panCanvasToCoordinates,
      calculateElementProgress,
      getExternalLock,
      verifyUserOperationClearance,
      creditTaskCompletionReward,
      createElement,
      createElements,
      updateElement,
      updateChecklistItem,
      applyStripboardDelta,
      updateChecklistPrerequisites,
      deleteElement,
      permanentlyDeleteElement,
      moveFromBin,
    }),
    [
      calculateElementProgress,
      activeCanvasNodeId,
      closeCanvasWorkspace,
      createElement,
      createElements,
      deleteElement,
      permanentlyDeleteElement,
      moveFromBin,
      elements,
      pathOnlyElementIds,
      isAccessRestricted,
      elementsLastSyncedAt,
      elementsSubscriptionError,
      refreshElementsSubscription,
      resolvedChecklistItems,
      canPerformOnNode,
      error,
      effectiveWorkspaceId,
      getExternalLock,
      isPublicReadOnly,
      isLoading,
      checklistsLoaded,
      visibleLocks,
      memberProfile,
      viewportState,
      canvasTransform,
      isCinematicModeActive,
      isCalendarShelfOpen,
      externalEventsArray,
      markdownMetadata,
      isGestureKnifeActive,
      isCanvasMaximized,
      maximizeCanvasWorkspace,
      panCanvasToCoordinates,
      selectedElementId,
      setCanvasTransform,
      updateElement,
      updateChecklistItem,
      applyStripboardDelta,
      updateChecklistPrerequisites,
      verifyUserOperationClearance,
      creditTaskCompletionReward,
    ],
  )

  return <TimelineContext.Provider value={value}>{children}</TimelineContext.Provider>
}
